import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiCheck, FiCalendar, FiFilter, FiX, FiUser, FiPrinter, FiSearch, FiPlus, FiMinus, FiCreditCard, FiDollarSign, FiEdit2, FiTrash2, FiChevronRight, FiFileText, FiGift, FiDownload, FiShare2, FiBook, FiAlertCircle } from 'react-icons/fi';
import { useSocket } from '../Layout';
import ShareModal from './components/ShareModal';
import OrderDetailModal from './components/OrderDetailModal';
import PaymentSelectionModal from './components/PaymentSelectionModal';
import CustomerSelectionModal from './components/CustomerSelectionModal';
import MobileCreateOrderContent from './components/MobileCreateOrderContent';
import Portal from './components/Portal';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';

// Helper function to get axios config with auth token
const getAuthConfig = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// Helper function to check if user is super admin from JWT token
const checkIsSuperAdmin = () => {
  try {
    const token = localStorage.getItem('adminToken');
    if (!token) return false;
    
    // Decode JWT payload (base64)
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.isEnvAdmin === true;
  } catch (e) {
    return false;
  }
};

// Helper function to remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

function OrderManagement() {
  const location = useLocation();
  const { socket } = useSocket();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const highlightOrderRef = useRef(null);
  
  // Get user role
  const userRole = localStorage.getItem('userRole') || 'admin';
  const isSalesRole = userRole === 'sales';
  const isSuperAdmin = checkIsSuperAdmin();
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(false);
  
  // Additional filters
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('all');
  const [, setSelectedDate] = useState('');

  // Paid orders filter and pagination (server-side)
  const [paidPaymentFilter, setPaidPaymentFilter] = useState('all'); // all, cash, transfer
  const [paidDateFrom, setPaidDateFrom] = useState('');
  const [paidDateTo, setPaidDateTo] = useState('');
  const [paidSearchTerm, setPaidSearchTerm] = useState('');
  const [debouncedPaidSearch, setDebouncedPaidSearch] = useState('');
  const [paidOrdersPage, setPaidOrdersPage] = useState(1);
  const [paidOrdersPerPage] = useState(20);
  const paidOrdersListRef = useRef(null);
  const paidSearchDebounceRef = useRef(null);
  const [paidOrders, setPaidOrders] = useState([]); // Separate state for paid orders
  const [paidOrdersTotal, setPaidOrdersTotal] = useState(0);
  const [paidTotalRevenue, setPaidTotalRevenue] = useState(0); // Total revenue from server
  const [paidCashTotal, setPaidCashTotal] = useState(0); // Cash total from server
  const [paidTransferTotal, setPaidTransferTotal] = useState(0); // Transfer total from server
  const [paidDebtTotal, setPaidDebtTotal] = useState(0); // Debt total from server
  const [paidPointsDiscountTotal, setPaidPointsDiscountTotal] = useState(0); // Points discount total from server
  const [hasMorePaidOrders, setHasMorePaidOrders] = useState(false);
  const [loadingMorePaid, setLoadingMorePaid] = useState(false);

  // Payment pending selection modal (when multiple orders with same amount)
  const [showPaymentSelectionModal, setShowPaymentSelectionModal] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [selectedPendingOrderId, setSelectedPendingOrderId] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  // Customer selection
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [selectedOrderForCustomer, setSelectedOrderForCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const [newCustomerForm, setNewCustomerForm] = useState({ name: '', phone: '' });

  // Shop settings for print
  const [shopSettings, setShopSettings] = useState(null);

  // Create order modal
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [toppings, setToppings] = useState([]);
  const [createOrderForm, setCreateOrderForm] = useState({
    tableId: '',
    items: [],
    customerNote: '',
    customerId: '',
    deliveryDate: '',
    deliveryTime: '',
    extraFee: {
      amount: 0,
      description: '',
      excludeFromRevenue: false,
      expenseGroup: '',
      expenseGroupName: '',
      recipient: ''
    }
  });
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [selectedMenuCategory, setSelectedMenuCategory] = useState('all');
  const [selectedCustomerDiscount, setSelectedCustomerDiscount] = useState(null);
  const [activeDiscounts, setActiveDiscounts] = useState([]); // List of active discounts for selection
  
  // Manual discount state for desktop
  const [desktopManualDiscountType, setDesktopManualDiscountType] = useState('fixed'); // 'fixed' or 'percent'
  const [desktopManualDiscountValue, setDesktopManualDiscountValue] = useState('');
  
  // Scroll load for menu items
  const [displayedItemsCount, setDisplayedItemsCount] = useState(20);
  const menuListRef = useRef(null);
  
  // Item being configured (with options)
  const [configuringItem, setConfiguringItem] = useState(null);
  const [itemOptions, setItemOptions] = useState({
    temperature: 'Mặc định',
    sweetness: 'Mặc định',
    selectedOption: null,
    selectedToppings: [],
    quantity: 1
  });

  // Payment method modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountReceived, setAmountReceived] = useState('');
  const [paymentCustomer, setPaymentCustomer] = useState(null);
  const [paymentCustomerSearch, setPaymentCustomerSearch] = useState('');
  const [paymentCustomerResults, setPaymentCustomerResults] = useState([]);
  const [showNewCustomerInPayment, setShowNewCustomerInPayment] = useState(false);
  const [newPaymentCustomer, setNewPaymentCustomer] = useState({ name: '', phone: '', notes: '' });
  const [processingPayment, setProcessingPayment] = useState(false);
  const [changeReturned, setChangeReturned] = useState(true); // Đã trả lại tiền thừa (mặc định true)
  const [keepChange, setKeepChange] = useState(false); // Giữ tiền thừa cho khách
  const [saveBalanceAmount, setSaveBalanceAmount] = useState(''); // Số tiền thừa muốn lưu
  const [useBalance, setUseBalance] = useState(false); // Sử dụng tiền thừa có sẵn
  
  // Points states
  const [pointSettings, setPointSettings] = useState(null);
  const [usePoints, setUsePoints] = useState(0);
  const [maxRedeemablePoints, setMaxRedeemablePoints] = useState(0);

  // Bank accounts states
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);

  // New customer in create order modal
  const [showNewCustomerInOrder, setShowNewCustomerInOrder] = useState(false);
  const [createOrderCustomerSearch, setCreateOrderCustomerSearch] = useState('');
  const [showCustomerSearchDropdown, setShowCustomerSearchDropdown] = useState(false);
  const [createOrderCustomerResults, setCreateOrderCustomerResults] = useState([]);

  // Extra fee for create order
  const [expenseGroups, setExpenseGroups] = useState([]);
  const [expenseRecipients, setExpenseRecipients] = useState([]);
  const [salesAccounts, setSalesAccounts] = useState([]);

  // Edit order items
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editingOrderItems, setEditingOrderItems] = useState([]); // Local copy of items being edited
  const [editingOrderDiscount, setEditingOrderDiscount] = useState(null); // Selected discount for editing order
  const [editingOrderExtraFee, setEditingOrderExtraFee] = useState({ amount: 0, description: '', excludeFromRevenue: false, expenseGroup: '', expenseGroupName: '', recipient: '' });
  const [editingOrderManualDiscount, setEditingOrderManualDiscount] = useState({ type: 'fixed', value: '', amount: 0 }); // Manual discount for editing

  // Delete order with password
  const [showDeleteOrderModal, setShowDeleteOrderModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');

  // Bulk delete for paid orders
  const [selectedPaidOrders, setSelectedPaidOrders] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeletePassword, setBulkDeletePassword] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [paidBulkSelectMode, setPaidBulkSelectMode] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Long press refs for paid orders
  const paidLongPressTimer = useRef(null);
  const paidIsLongPress = useRef(false);

  // Order detail modal for paid orders
  const [showOrderDetailModal, setShowOrderDetailModal] = useState(false);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);

  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedOrderForShare, setSelectedOrderForShare] = useState(null);

  // Recipe modal for viewing dish preparation steps
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [recipesWithSteps, setRecipesWithSteps] = useState({}); // Map: menuItemId_sizeOption -> boolean

  // Remote printer status
  const [printerOnline, setPrinterOnline] = useState(false);
  const [remotePrinting, setRemotePrinting] = useState(false);

  // Add item to existing order
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [orderToAddItem, setOrderToAddItem] = useState(null);
  const [addItemSearch, setAddItemSearch] = useState('');
  const [addItemCategory, setAddItemCategory] = useState('all');
  const [addItemConfiguring, setAddItemConfiguring] = useState(null);
  const [addItemOptions, setAddItemOptions] = useState({
    temperature: 'Mặc định',
    sweetness: 'Mặc định',
    selectedOption: null,
    selectedToppings: [],
    quantity: 1
  });

  useEffect(() => {
    loadOrders();
    loadTables();
    loadPointSettings();
    loadExpenseGroups();
    loadExpenseRecipients();
    loadSalesAccounts();
    
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Load recipes for pending/preparing orders
  useEffect(() => {
    const loadRecipesForOrders = async () => {
      // Only load for pending/preparing tabs
      if (filter !== 'pending' && filter !== 'preparing') return;
      
      // Get all unique menu items from orders
      const menuItemIds = new Set();
      orders.forEach(order => {
        if (order.status === 'pending' || order.status === 'preparing') {
          order.items?.forEach(item => {
            const menuItemId = item.menuItem?._id || item.menuItem;
            if (menuItemId) menuItemIds.add(menuItemId.toString());
          });
        }
      });
      
      if (menuItemIds.size === 0) return;
      
      try {
        // Fetch all dish recipes (limit=0 to get all)
        const response = await axios.get(`${API_URL}/recipes?type=dish&limit=0`);
        if (response.data.success && response.data.data) {
          const recipes = response.data.data;
          const recipeMap = {};
          
          recipes.forEach(recipe => {
            // Get menuItemId - it can be populated object or just ID
            let menuItemId = null;
            if (recipe.menuItemId) {
              menuItemId = typeof recipe.menuItemId === 'object' 
                ? (recipe.menuItemId._id || recipe.menuItemId) 
                : recipe.menuItemId;
              if (menuItemId) menuItemId = menuItemId.toString();
            }
            
            if (!menuItemId) return;
            
            const sizeOption = recipe.sizeOption || null;
            const key = `${menuItemId}_${sizeOption || 'default'}`;
            recipeMap[key] = true;
            
            // Also mark fallback as available if no size (default recipe)
            if (!sizeOption) {
              recipeMap[`${menuItemId}_fallback`] = true;
            }
          });
          
          setRecipesWithSteps(recipeMap);
        }
      } catch (error) {
        console.error('Error loading recipes for orders:', error);
      }
    };
    
    loadRecipesForOrders();
  }, [orders, filter]);

  // Load point settings
  const loadPointSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/points/settings`);
      if (response.data.success) {
        setPointSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error loading point settings:', error);
    }
  };

  const loadExpenseGroups = async () => {
    try {
      const response = await axios.get(`${API_URL}/expenses/groups`);
      setExpenseGroups(response.data || []);
    } catch (error) {
      console.error('Error loading expense groups:', error);
    }
  };

  const loadExpenseRecipients = async () => {
    try {
      const response = await axios.get(`${API_URL}/expenses/recipients`);
      setExpenseRecipients(response.data || []);
    } catch (error) {
      console.error('Error loading expense recipients:', error);
    }
  };

  const loadSalesAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      if (response.data.success && response.data.data?.additionalAdmins) {
        // Lấy tài khoản bán hàng (role = 'sales')
        const accounts = response.data.data.additionalAdmins
          .filter(admin => admin.role === 'sales')
          .map(admin => ({
            _id: admin._id,
            username: admin.username,
            fullName: admin.fullName || '',
            role: admin.role,
            displayName: admin.fullName ? `${admin.fullName} (${admin.username})` : admin.username
          }));
        setSalesAccounts(accounts);
      }
    } catch (error) {
      console.error('Error loading sales accounts:', error);
    }
  };

  // Calculate max redeemable points when customer changes
  useEffect(() => {
    const calculateMaxPoints = async () => {
      if (!selectedOrderForPayment || !paymentCustomer || !pointSettings?.isEnabled) {
        setMaxRedeemablePoints(0);
        return;
      }
      
      try {
        const response = await axios.post(`${API_URL}/points/max-redeem`, {
          customerId: paymentCustomer._id,
          orderAmount: selectedOrderForPayment.totalAmount,
          hasPromotion: !!selectedOrderForPayment.discount
        });
        
        if (response.data.success && response.data.data) {
          setMaxRedeemablePoints(response.data.data.maxPoints || 0);
        }
      } catch (error) {
        console.error('Error calculating max points:', error);
        setMaxRedeemablePoints(0);
      }
    };
    
    calculateMaxPoints();
  }, [selectedOrderForPayment, paymentCustomer, pointSettings]);

  // Debounce paid search term
  useEffect(() => {
    if (paidSearchDebounceRef.current) {
      clearTimeout(paidSearchDebounceRef.current);
    }
    paidSearchDebounceRef.current = setTimeout(() => {
      setDebouncedPaidSearch(paidSearchTerm);
    }, 400);
    return () => {
      if (paidSearchDebounceRef.current) {
        clearTimeout(paidSearchDebounceRef.current);
      }
    };
  }, [paidSearchTerm]);

  // Reload paid orders when filters change (server-side filtering)
  useEffect(() => {
    if (filter === 'paid') {
      loadPaidOrders(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidDateFrom, paidDateTo, paidPaymentFilter, debouncedPaidSearch]);

  // Listen for socket events to refresh orders
  useEffect(() => {
    if (socket) {
      const handleRefresh = () => loadOrders();
      
      // Handler cho payment-received - hiển thị toast thông báo
      const handlePaymentReceived = (data) => {
        toast.success(`💳 ${data.message}`, { autoClose: 5000 });
        loadOrders(); // Refresh danh sách
      };
      
      // Handler cho payment-pending-selection - hiển thị modal chọn đơn hàng
      const handlePaymentPendingSelection = (data) => {
        toast.warning(`⚠️ ${data.message}`, { autoClose: 8000 });
        setPendingPaymentData(data);
        setSelectedPendingOrderId(null);
        setShowPaymentSelectionModal(true);
      };
      
      socket.on('new-order', handleRefresh);
      socket.on('order-updated', handleRefresh);
      socket.on('payment-completed', handleRefresh);
      socket.on('payment-received', handlePaymentReceived);
      socket.on('payment-pending-selection', handlePaymentPendingSelection);
      socket.on('orders-cleared', handleRefresh);
      
      // Listen for bank account switch (from cron job)
      socket.on('bank-account-switched', (data) => {
        console.log('[Bank Switch] Received:', data);
        loadShopSettings(); // Reload shop settings to get new default bank
        toast.info(`🏦 Đã chuyển tài khoản mặc định sang ${data.bankName}`, { autoClose: 5000 });
      });
      
      // Listen for printer status
      socket.on('printer-status', (data) => {
        setPrinterOnline(data.online);
        // Removed: toast.success('🖨️ Máy in đã kết nối')
      });
      
      // Request current printer status on load
      socket.emit('check-printer-status');
      
      // Listen for print completion
      socket.on('print-status', (data) => {
        setRemotePrinting(false);
        if (data.status === 'error') {
          toast.error(`❌ Lỗi in: ${data.message}`);
        }
        // Removed: toast.success('✅ In thành công!')
      });
      
      return () => {
        socket.off('new-order', handleRefresh);
        socket.off('order-updated', handleRefresh);
        socket.off('payment-completed', handleRefresh);
        socket.off('payment-received', handlePaymentReceived);
        socket.off('payment-pending-selection', handlePaymentPendingSelection);
        socket.off('orders-cleared', handleRefresh);
        socket.off('bank-account-switched');
        socket.off('printer-status');
        socket.off('print-status');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, filter]);

  // Track if we've processed the switchToTab from navigation
  const switchTabProcessedRef = useRef(false);

  // Switch tab when navigated from notification
  useEffect(() => {
    if (location.state?.switchToTab && !switchTabProcessedRef.current) {
      switchTabProcessedRef.current = true;
      setFilter(location.state.switchToTab);
    }
  }, [location.state?.switchToTab]);

  // Reset the ref when location changes (new navigation)
  useEffect(() => {
    switchTabProcessedRef.current = false;
  }, [location.key]);

  useEffect(() => {
    // Highlight order if navigated from notification
    if (location.state?.highlightOrderId) {
      setTimeout(() => {
        const element = highlightOrderRef.current;
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-4', 'ring-blue-500');
          setTimeout(() => {
            element.classList.remove('ring-4', 'ring-blue-500');
          }, 3000);
        }
      }, 500);
    }
  }, [orders, location.state?.highlightOrderId]);

  // Handle openCreateModal from navigation state (from Dashboard quick action)
  useEffect(() => {
    if (location.state?.openCreateModal && menuItems.length > 0 && categories.length > 0) {
      setShowCreateOrderModal(true);
      // Clear the state to prevent re-opening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.openCreateModal, menuItems.length, categories.length]);

  // Tick every 30s to refresh delivery countdown / border colors on order cards
  const [, setDeliveryTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setDeliveryTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const loadOrders = async () => {
    try {
      // For paid orders, use separate function with pagination
      if (filter === 'paid') {
        await loadPaidOrders(1, true); // Reset to page 1
        return;
      }
      
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get(`${API_URL}/orders`, { params });
      setOrders(response.data.data);
    } catch (error) {
      toast.error('Không thể tải danh sách đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  // Load paid orders with server-side pagination
  const loadPaidOrders = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMorePaid(true);
      }

      const params = {
        status: 'paid',
        page,
        limit: paidOrdersPerPage
      };

      if (paidDateFrom) params.startDate = paidDateFrom;
      if (paidDateTo) params.endDate = paidDateTo;
      if (paidPaymentFilter !== 'all') params.paymentMethod = paidPaymentFilter;
      if (debouncedPaidSearch) params.search = debouncedPaidSearch;
      
      // Send default bank account number for filtering logic
      // Backend will check: if default bank is primary bank, filter orders by primary bank only
      if (selectedBankAccount?.accountNumber) {
        params.defaultBankAccountNumber = selectedBankAccount.accountNumber;
      }

      const config = getAuthConfig();
      const response = await axios.get(`${API_URL}/orders`, { params, ...config });
      const { data, pagination } = response.data;

      if (reset || page === 1) {
        setPaidOrders(data);
        setPaidTotalRevenue(pagination?.totalRevenue || 0);
        setPaidCashTotal(pagination?.cashTotal || 0);
        setPaidTransferTotal(pagination?.transferTotal || 0);
        setPaidDebtTotal(pagination?.debtTotal || 0);
        setPaidPointsDiscountTotal(pagination?.pointsDiscountTotal || 0);
      } else {
        setPaidOrders(prev => [...prev, ...data]);
      }

      setPaidOrdersPage(page);
      setPaidOrdersTotal(pagination?.total || 0);
      setHasMorePaidOrders(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải danh sách đơn hàng đã thanh toán');
    } finally {
      setLoading(false);
      setLoadingMorePaid(false);
    }
  };

  // Export paid orders to Excel
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      const params = {
        status: 'paid'
      };
      
      if (paidDateFrom) params.startDate = paidDateFrom;
      if (paidDateTo) params.endDate = paidDateTo;
      if (paidPaymentFilter !== 'all') params.paymentMethod = paidPaymentFilter;
      if (debouncedPaidSearch) params.search = debouncedPaidSearch;
      
      const config = getAuthConfig();
      const response = await axios.get(`${API_URL}/orders/export/excel`, {
        params,
        responseType: 'blob',
        ...config
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Bao_cao_don_hang.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Đã xuất file Excel thành công');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Không thể xuất file Excel');
    } finally {
      setExporting(false);
    }
  };

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API_URL}/tables`);
      // Sort tables numerically by tableNumber
      const sortedTables = (response.data.data || []).sort((a, b) => {
        const numA = parseInt(a.tableNumber, 10) || 0;
        const numB = parseInt(b.tableNumber, 10) || 0;
        return numA - numB;
      });
      setTables(sortedTables);
    } catch (error) {
      console.error('Error loading tables:', error);
    }
  };

  const loadCustomers = async (search = '') => {
    try {
      const response = await axios.get(`${API_URL}/customers`, {
        params: { search, limit: 20 }
      });
      setCustomers(response.data.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadShopSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop-settings`);
      setShopSettings(response.data.data);
      
      // Set bank accounts - super admin sees all, regular admin sees only primary bank
      if (response.data.data?.bankAccounts) {
        const allBanks = response.data.data.bankAccounts;
        
        if (isSuperAdmin) {
          // Super admin sees all bank accounts
          setBankAccounts(allBanks);
          const defaultBank = allBanks.find(b => b.isDefault);
          setSelectedBankAccount(defaultBank || allBanks[0] || null);
        } else {
          // Regular admin only sees primary bank (isPrimary = true)
          const primaryBank = allBanks.find(b => b.isPrimary);
          if (primaryBank) {
            setBankAccounts([primaryBank]);
            setSelectedBankAccount(primaryBank);
          } else {
            // Fallback to default bank if no primary
            const defaultBank = allBanks.find(b => b.isDefault);
            if (defaultBank) {
              setBankAccounts([defaultBank]);
              setSelectedBankAccount(defaultBank);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading shop settings:', error);
    }
  };

  const loadMenuItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/menu`);
      setMenuItems(response.data.data || []);
    } catch (error) {
      console.error('Error loading menu:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadToppings = async () => {
    try {
      const response = await axios.get(`${API_URL}/toppings`);
      setToppings(response.data.data || []);
    } catch (error) {
      console.error('Error loading toppings:', error);
    }
  };

  // Load customers and shop settings on mount
  useEffect(() => {
    loadCustomers();
    loadShopSettings();
    loadMenuItems();
    loadCategories();
    loadToppings();
  }, []);

  // Search customers with debounce
  useEffect(() => {
    if (showCustomerModal) {
      const timer = setTimeout(() => {
        loadCustomers(customerSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [customerSearch, showCustomerModal]);

  const openCustomerModal = (order) => {
    setSelectedOrderForCustomer(order);
    setCustomerSearch('');
    setShowAddCustomerForm(false);
    setNewCustomerForm({ name: '', phone: '' });
    loadCustomers();
    setShowCustomerModal(true);
  };

  const assignCustomer = async (customerId) => {
    try {
      await axios.put(`${API_URL}/orders/${selectedOrderForCustomer._id}/assign-customer`, { customerId });
      setShowCustomerModal(false);
      loadOrders();
    } catch (error) {
      toast.error('Không thể gán khách hàng');
    }
  };

  const removeCustomer = async (orderId) => {
    try {
      await axios.put(`${API_URL}/orders/${orderId}/remove-customer`);
      loadOrders();
    } catch (error) {
      toast.error('Không thể xóa khách hàng');
    }
  };

  // Bulk delete functions
  const toggleSelectPaidOrder = (orderId) => {
    setSelectedPaidOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleSelectAllPaidOrders = () => {
    if (selectedPaidOrders.length === paidOrders.length) {
      setSelectedPaidOrders([]);
    } else {
      setSelectedPaidOrders(paidOrders.map(order => order._id));
    }
  };

  const handleBulkDelete = async () => {
    if (!bulkDeletePassword) {
      toast.error('Vui lòng nhập mật khẩu');
      return;
    }

    setBulkDeleting(true);
    try {
      const response = await axios.post(`${API_URL}/orders/bulk-delete`, {
        orderIds: selectedPaidOrders,
        password: bulkDeletePassword
      });
      
      setSelectedPaidOrders([]);
      setShowBulkDeleteModal(false);
      setBulkDeletePassword('');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể xóa đơn hàng');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAddNewCustomer = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/customers`, newCustomerForm);
      const newCustomer = response.data.data;
      // Assign to order immediately
      await assignCustomer(newCustomer._id);
    } catch (error) {
      toast.error('Không thể thêm khách hàng');
    }
  };

  const printReceipt = (order) => {
    // Load print settings from localStorage
    const savedPrintSettings = localStorage.getItem('printSettings');
    const printSettings = savedPrintSettings ? JSON.parse(savedPrintSettings) : {
      showLogo: true,
      showQRCode: true,
      showBankInfo: true,
      fontSize: 'normal',
      footerText: 'Cảm ơn quý khách!',
      showTransferNote: true,
      paperWidth: '80mm'
    };

    // Font size mapping
    const fontSizes = {
      small: { body: '12px', shopName: '14px', title: '13px', item: '11px', option: '10px', total: '13px' },
      normal: { body: '14px', shopName: '16px', title: '15px', item: '13px', option: '12px', total: '15px' },
      large: { body: '16px', shopName: '18px', title: '17px', item: '15px', option: '14px', total: '17px' }
    };
    const fs = fontSizes[printSettings.fontSize] || fontSizes.normal;

    const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
    const shopAvatar = shopSettings?.avatar ? `${API_URL.replace('/api', '')}${shopSettings.avatar}` : null;
    
    // Generate order code - use stored orderCode if available, otherwise generate
    const orderCode = order.orderCode || `${order.tableNumber || 'DH'}${new Date(order.createdAt).getTime().toString().slice(-6)}`;
    
    // Generate VietQR URL if bank account exists and QR is enabled
    let qrCodeUrl = '';
    const transferPrefix = printSettings.transferPrefix || '';
    // Nội dung CK = prefix (nếu có) + mã đơn hàng
    const transferContent = transferPrefix ? `${transferPrefix} ${orderCode}` : orderCode;
    
    if (printSettings.showQRCode && defaultBank) {
      const bank = VIETNAM_BANKS.find(b => b.code === defaultBank.bankCode);
      if (bank) {
        qrCodeUrl = `https://img.vietqr.io/image/${bank.bin}-${defaultBank.accountNumber}-compact.png?amount=${order.totalAmount}&addInfo=${encodeURIComponent(transferContent)}`;
      }
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Hóa đơn</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          
          @page {
            size: ${printSettings.paperWidth} auto;
            margin: 0;
          }
          
          html, body { 
            width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
          }
          
          body { 
            font-family: Arial, sans-serif;
            font-size: ${fs.body};
            line-height: 1.2;
            background: #fff;
            color: #000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .receipt { 
            padding: 4px;
            width: 100%;
          }
          
          .header { 
            text-align: center; 
            margin-bottom: 4px;
          }
          
          .logo { 
            width: 40px; 
            height: 40px; 
            border-radius: 50%; 
            margin: 0 auto 3px;
            display: block;
          }
          
          .logo-placeholder {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #16a34a;
            margin: 0 auto 3px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            font-weight: bold;
            font-size: 16px;
          }
          
          .shop-name { 
            font-size: ${fs.shopName}; 
            font-weight: bold;
            margin-bottom: 1px;
          }
          
          .shop-phone {
            font-size: ${fs.item};
          }
          
          .divider { 
            border: none;
            border-top: 1px dashed #000; 
            margin: 4px 0;
          }
          
          .title { 
            text-align: center; 
            font-weight: bold; 
            font-size: ${fs.title};
            margin-bottom: 1px;
          }
          
          .subtitle {
            text-align: center;
            font-size: ${fs.option};
            margin-bottom: 3px;
          }
          
          .info-row { 
            display: flex; 
            justify-content: space-between;
            margin-bottom: 2px;
            font-size: ${fs.item};
          }
          
          .item { 
            margin-bottom: 4px;
          }
          
          .item-name { 
            font-weight: bold;
            font-size: ${fs.item};
          }
          
          .item-options { 
            font-size: ${fs.option}; 
            color: #000; 
            padding-left: 8px;
          }
          
          .item-qty { 
            display: flex; 
            justify-content: space-between;
            font-size: ${fs.item};
            margin-top: 2px;
          }
          
          .total-section {
            margin-top: 3px;
          }
          
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 2px;
            font-size: ${fs.item};
          }
          
          .total-row.discount {
            font-size: ${fs.option};
          }
          
          .total-row.extra-fee {
            font-size: ${fs.option};
          }
          
          .total-row.grand-total {
            font-weight: bold;
            font-size: ${fs.total};
            border-top: 1px solid #000;
            padding-top: 3px;
            margin-top: 3px;
          }
          
          .qr-section { 
            display: flex;
            align-items: center;
            margin: 4px 0;
            gap: 8px;
          }
          
          .qr-code { 
            width: ${printSettings.qrSize || 150}px; 
            height: ${printSettings.qrSize || 150}px;
            flex-shrink: 0;
          }
          
          .bank-info { 
            font-size: ${fs.option}; 
            text-align: left;
            flex: 1;
          }
          
          .bank-name {
            font-weight: bold;
            font-size: ${fs.option};
            margin-bottom: 2px;
          }
          
          .bank-account {
            font-size: ${fs.option};
            margin-bottom: 1px;
          }
          
          .footer { 
            text-align: center; 
            margin-top: 4px; 
            font-size: ${fs.option};
          }
          
          .transfer-note {
            text-align: center;
            font-size: ${fs.option};
            margin-top: 3px;
            padding: 3px;
            border: 1px dashed #000;
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            ${printSettings.showLogo ? (shopAvatar ? `<img src="${shopAvatar}" class="logo" alt="Logo">` : '<div class="logo-placeholder">GC</div>') : ''}
            <div class="shop-name">${shopSettings?.shopName || 'Green Cafe'}</div>
            <div class="shop-phone">${shopSettings?.phone || ''}</div>
          </div>
          
          <hr class="divider">
          
          <div class="title">HÓA ĐƠN BÁN HÀNG</div>
          <div class="subtitle">${orderCode} - ${new Date().toLocaleString('vi-VN')}</div>
          
          <div class="info-row">
            <span>Khách:</span>
            <span>${order.customer?.name || order.customerName || (order.table?.name || (order.tableNumber ? 'Bàn ' + order.tableNumber : 'Mang đi'))}</span>
          </div>
          
          <hr class="divider">
          
          <div class="items">
            ${order.items.map((item, idx) => {
              // Tính giá option nếu có
              const optionPrice = item.selectedOption?.price || 0;
              const toppingsPrice = item.selectedToppings?.reduce((sum, t) => sum + (t.price || 0), 0) || 0;
              const basePrice = item.price - toppingsPrice - optionPrice;
              const itemTotal = item.price * item.quantity;
              
              return `
              <div class="item">
                <div class="item-name">${idx + 1}. ${item.name} (${basePrice.toLocaleString('vi-VN')}đ)</div>
                ${item.selectedOption ? `
                  <div class="item-options">- ${item.selectedOption.name}${optionPrice > 0 ? ` (+${optionPrice.toLocaleString('vi-VN')}đ)` : ''}</div>
                ` : ''}
                ${item.selectedToppings && item.selectedToppings.length > 0 ? item.selectedToppings.map(t => `
                  <div class="item-options">- ${t.name}${t.price > 0 ? ` (+${t.price.toLocaleString('vi-VN')}đ)` : ''}</div>
                `).join('') : ''}
                <div class="item-qty">
                  <span>x${item.quantity}</span>
                  <span>${formatCurrency(itemTotal)}</span>
                </div>
              </div>
            `}).join('')}
          </div>
          
          <hr class="divider">
          
          <div class="total-section">
            ${(() => {
              const promotionDiscount = order.discount?.amount || 0;
              const manualDiscountAmount = order.manualDiscount?.amount || 0;
              const extraFeeAmount = order.extraFee?.amount || 0;
              const hasPromotionDiscount = promotionDiscount > 0;
              const hasManualDiscount = manualDiscountAmount > 0;
              const hasAnyDiscount = hasPromotionDiscount || hasManualDiscount;
              const itemsSubtotal = order.totalAmount - extraFeeAmount + promotionDiscount + manualDiscountAmount;
              
              let html = '';
              
              // Show original price if has any discount
              if (hasAnyDiscount) {
                html += `
                <div class="total-row">
                  <span>Giá gốc</span>
                  <span>${formatCurrency(itemsSubtotal)}</span>
                </div>`;
              }
              
              // Show promotion discount
              if (hasPromotionDiscount) {
                html += `
                <div class="total-row discount">
                  <span>🎁 ${order.discount.name || 'Giảm ' + order.discount.percentage + '%'}</span>
                  <span>-${formatCurrency(promotionDiscount)}</span>
                </div>`;
              }
              
              // Show manual discount
              if (hasManualDiscount) {
                const discountLabel = order.manualDiscount.discountType === 'percent' 
                  ? order.manualDiscount.value + '%' 
                  : 'cố định';
                html += `
                <div class="total-row discount">
                  <span>🏷️ Giảm giá (${discountLabel})</span>
                  <span>-${formatCurrency(manualDiscountAmount)}</span>
                </div>`;
              }
              
              // Show extra fee
              if (extraFeeAmount > 0) {
                if (hasAnyDiscount) {
                  html += `
                  <div class="total-row">
                    <span>Tạm tính</span>
                    <span>${formatCurrency(itemsSubtotal - promotionDiscount - manualDiscountAmount)}</span>
                  </div>`;
                }
                html += `
                <div class="total-row extra-fee">
                  <span>🚚 Phụ phí${order.extraFee.description ? ' (' + order.extraFee.description + ')' : ''}</span>
                  <span>+${formatCurrency(extraFeeAmount)}</span>
                </div>`;
              }
              
              return html;
            })()}
            <div class="total-row grand-total">
              <span>Tổng cộng (${order.items.reduce((sum, i) => sum + i.quantity, 0)}SP)</span>
              <span>${formatCurrency(order.totalAmount)}</span>
            </div>
          </div>
          
          ${qrCodeUrl ? `
            <hr class="divider">
            <div class="qr-section">
              <img src="${qrCodeUrl}" class="qr-code" alt="VietQR">
              ${printSettings.showBankInfo && defaultBank ? `
              <div class="bank-info">
                <div class="bank-name">${defaultBank.bankName}</div>
                <div class="bank-account">${defaultBank.accountName}</div>
                <div class="bank-account"><b>${defaultBank.accountNumber}</b></div>
                <div class="bank-account" style="margin-top: 3px;">ND: <b>${transferContent}</b></div>
              </div>
              ` : ''}
            </div>
          ` : ''}
          
          ${printSettings.showTransferNote ? `
          <hr class="divider">
          <div class="transfer-note">
            Thanh toán tiền mặt hoặc chuyển khoản
          </div>
          ` : ''}
          
          ${printSettings.footerText ? `
          <div class="footer">
            ${printSettings.footerText}
          </div>
          ` : ''}
        </div>
      </body>
      </html>
    `;

    // Create hidden iframe for printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.top = '-10000px';
    printFrame.style.left = '-10000px';
    printFrame.style.width = '302px'; // 80mm at 96dpi
    printFrame.style.height = 'auto';
    document.body.appendChild(printFrame);

    const frameDoc = printFrame.contentWindow || printFrame.contentDocument;
    const doc = frameDoc.document || frameDoc;
    
    doc.open();
    doc.write(html);
    doc.close();

    // Wait for images to load then print
    const images = doc.querySelectorAll('img');
    let loadedImages = 0;
    const totalImages = images.length;
    let hasPrinted = false; // Flag to prevent double print

    const triggerPrint = () => {
      if (hasPrinted) return; // Prevent double print
      hasPrinted = true;
      
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Remove iframe after print dialog closes
        setTimeout(() => {
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        }, 1000);
      }, 300);
    };

    if (totalImages === 0) {
      triggerPrint();
    } else {
      images.forEach(img => {
        if (img.complete) {
          loadedImages++;
          if (loadedImages === totalImages) triggerPrint();
        } else {
          img.onload = img.onerror = () => {
            loadedImages++;
            if (loadedImages === totalImages) triggerPrint();
          };
        }
      });
      // Fallback after 2 seconds
      setTimeout(triggerPrint, 2000);
    }
  };

  // Remote print - send to Electron app via socket
  const remotePrint = (order) => {
    if (!socket) {
      toast.error('Không có kết nối socket');
      return;
    }
    
    if (!printerOnline) {
      toast.error('Máy in không online. Hãy đảm bảo ứng dụng Electron đang chạy tại cửa hàng.');
      return;
    }
    
    setRemotePrinting(true);
    
    // Load print settings
    const savedPrintSettings = localStorage.getItem('printSettings');
    const printSettings = savedPrintSettings ? JSON.parse(savedPrintSettings) : {
      showLogo: true,
      showQRCode: true,
      showBankInfo: true,
      fontSize: 'normal',
      footerText: 'Cảm ơn quý khách!',
      showTransferNote: true,
      paperWidth: '80mm',
      transferPrefix: ''
    };
    
    // Get bank info for QR
    const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
    
    // Generate order code - use stored orderCode if available, otherwise generate
    const orderCode = order.orderCode || `${order.tableNumber || 'DH'}${new Date(order.createdAt).getTime().toString().slice(-6)}`;
    
    // Send print request via socket
    socket.emit('print-request', {
      orderId: order._id,
      orderCode,
      order: {
        ...order,
        tableName: order.tableName || tables.find(t => t._id === order.tableId)?.name,
        customerName: order.customer?.name || order.customerName
      },
      shopSettings: {
        shopName: shopSettings?.shopName,
        address: shopSettings?.address,
        phone: shopSettings?.phone,
        avatar: shopSettings?.avatar,
        bankAccounts: shopSettings?.bankAccounts?.map(b => ({
          ...b,
          bin: VIETNAM_BANKS.find(vb => vb.code === b.bankCode)?.bin,
          bankName: VIETNAM_BANKS.find(vb => vb.code === b.bankCode)?.name || b.bankCode
        }))
      },
      printSettings
    });
    
    // Timeout after 10 seconds
    setTimeout(() => {
      setRemotePrinting(false);
    }, 10000);
  };

  // Smart print - auto use remote print on mobile
  const handlePrint = (order) => {
    if (isMobile) {
      // Mobile always uses remote print
      if (!printerOnline) {
        toast.error('🔴 Máy in chưa kết nối. Vui lòng kiểm tra ứng dụng tại quán.');
        return;
      }
      remotePrint(order);
    } else {
      // Desktop can print locally
      printReceipt(order);
    }
  };

  // Vietnam banks for QR
  const VIETNAM_BANKS = [
    { code: 'VCB', name: 'Vietcombank', bin: '970436' },
    { code: 'TCB', name: 'Techcombank', bin: '970407' },
    { code: 'MB', name: 'MB Bank', bin: '970422' },
    { code: 'VTB', name: 'VietinBank', bin: '970415' },
    { code: 'BIDV', name: 'BIDV', bin: '970418' },
    { code: 'ACB', name: 'ACB', bin: '970416' },
    { code: 'STB', name: 'Sacombank', bin: '970403' },
    { code: 'VPB', name: 'VPBank', bin: '970432' },
    { code: 'TPB', name: 'TPBank', bin: '970423' },
    { code: 'AGR', name: 'Agribank', bin: '970405' },
    { code: 'SHB', name: 'SHB', bin: '970443' },
    { code: 'HDB', name: 'HDBank', bin: '970437' },
    { code: 'OCB', name: 'OCB', bin: '970448' },
    { code: 'SEA', name: 'SeABank', bin: '970440' },
    { code: 'LPB', name: 'LienVietPostBank', bin: '970449' },
    { code: 'VIB', name: 'VIB', bin: '970441' },
    { code: 'MSB', name: 'MSB', bin: '970426' },
    { code: 'EIB', name: 'Eximbank', bin: '970431' },
    { code: 'BAB', name: 'Bac A Bank', bin: '970409' },
    { code: 'NAB', name: 'Nam A Bank', bin: '970428' },
    { code: 'NCB', name: 'NCB', bin: '970419' },
    { code: 'KLB', name: 'Kienlongbank', bin: '970452' },
    { code: 'PGB', name: 'PGBank', bin: '970430' },
    { code: 'VAB', name: 'Viet A Bank', bin: '970427' },
    { code: 'SCB', name: 'SCB', bin: '970429' },
    { code: 'CAKE', name: 'CAKE by VPBank', bin: '546034' },
    { code: 'Ubank', name: 'Ubank by VPBank', bin: '546035' },
  ];

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Build request body
      const requestBody = { status: newStatus };
      
      // When confirming order (pending -> preparing), send soldBy info
      if (newStatus === 'preparing') {
        requestBody.soldBy = {
          odId: localStorage.getItem('adminId') || null,
          username: localStorage.getItem('userName') || 'admin',
          role: localStorage.getItem('userRole') || 'admin'
        };
      }
      
      const response = await axios.put(`${API_URL}/orders/${orderId}/status`, requestBody);

      // Backend may auto-convert preparing→completed to preparing→paid when paidAt is set
      const actualStatus = response.data?.data?.status || newStatus;

      // Load orders for the NEW tab first, then switch tab
      let newFilter = filter;
      if (newStatus === 'preparing') {
        newFilter = 'preparing';
      } else if (newStatus === 'completed') {
        // If backend set it to 'paid' (order was pre-paid), switch to paid tab
        newFilter = actualStatus === 'paid' ? 'paid' : 'completed';
      }
      
      // Fetch orders for new tab
      const statusFilter = newFilter === 'all' ? '' : `?status=${newFilter}`;
      const ordersResponse = await axios.get(`${API_URL}/orders${statusFilter}`);
      
      if (newFilter === 'paid') {
        // For paid tab, trigger full paid orders reload via filter change
        setFilter(newFilter);
        setSelectedTable('all');
        setSelectedDate('');
      } else {
        setOrders(ordersResponse.data.data || []);
        // Now switch tab (data already loaded)
        setFilter(newFilter);
        setSelectedTable('all');
        setSelectedDate('');
      }
      
    } catch (error) {
      toast.error('Cập nhật trạng thái thất bại');
    }
  };

  // Open payment modal instead of direct confirm
  const openPaymentModal = (order) => {
    setSelectedOrderForPayment(order);
    setPaymentMethod('cash');
    // Auto-fill amount received with order total for cash payment
    setAmountReceived(order.totalAmount.toString());
    // Pre-fill customer if order has one
    if (order.customer) {
      const existingCustomer = customers.find(c => c._id === order.customer._id || c._id === order.customer);
      setPaymentCustomer(existingCustomer || order.customer);
    } else {
      setPaymentCustomer(null);
    }
    setPaymentCustomerSearch('');
    setPaymentCustomerResults([]);
    setShowNewCustomerInPayment(false);
    setNewPaymentCustomer({ name: '', phone: '', notes: '' });
    setChangeReturned(true); // Mặc định đã trả lại tiền thừa
    setKeepChange(false);
    setSaveBalanceAmount('');
    setUseBalance(false);
    setUsePoints(0); // Reset points
    // Reset bank account to default
    const defaultBank = bankAccounts.find(b => b.isDefault);
    setSelectedBankAccount(defaultBank || bankAccounts[0] || null);
    setShowPaymentModal(true);
  };

  // Search customers for payment
  const searchPaymentCustomers = async (search) => {
    setPaymentCustomerSearch(search);
    if (search.length >= 1) {
      const filtered = customers.filter(c => 
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        (c.phone && c.phone.includes(search))
      );
      setPaymentCustomerResults(filtered.slice(0, 5));
    } else {
      setPaymentCustomerResults([]);
    }
  };

  // Calculate payment amounts
  const calculatePaymentAmounts = () => {
    if (!selectedOrderForPayment) return { change: 0, debt: 0, balanceToUse: 0, remainingToPay: 0 };
    const total = selectedOrderForPayment.totalAmount;
    const customerBalance = paymentCustomer?.balance || 0;
    const pointsDiscountAmount = usePoints * (pointSettings?.pointValue || 500);
    const totalAfterDiscount = total - pointsDiscountAmount;
    const balanceToUse = useBalance ? Math.min(customerBalance, totalAfterDiscount) : 0;
    const remainingToPay = Math.max(0, totalAfterDiscount - balanceToUse);
    const received = parseFloat(amountReceived) || 0;
    
    if (paymentMethod === 'debt') {
      return { change: 0, debt: remainingToPay, balanceToUse, remainingToPay, pointsDiscountAmount };
    }
    if (paymentMethod === 'transfer') {
      return { change: 0, debt: 0, balanceToUse, remainingToPay, pointsDiscountAmount };
    }
    // Cash
    if (received >= remainingToPay) {
      return { change: received - remainingToPay, debt: 0, balanceToUse, remainingToPay, pointsDiscountAmount };
    }
    return { change: 0, debt: remainingToPay - received, balanceToUse, remainingToPay, pointsDiscountAmount };
  };

  const completePayment = async () => {
    if (!selectedOrderForPayment) return;
    
    const { change, debt, balanceToUse } = calculatePaymentAmounts();
    const balanceToSave = keepChange && saveBalanceAmount ? parseFloat(saveBalanceAmount) : 0;
    
    // Check if customer required for debt or points
    if ((debt > 0 || usePoints > 0) && !paymentCustomer && !showNewCustomerInPayment) {
      toast.error('Cần chọn hoặc tạo khách hàng để ghi nợ/dùng điểm');
      return;
    }
    
    if (showNewCustomerInPayment && !newPaymentCustomer.name.trim()) {
      toast.error('Vui lòng nhập tên khách hàng');
      return;
    }
    
    // Detect if this is a pre-payment for a preparing/pending order
    const isPrePay = ['preparing', 'pending'].includes(selectedOrderForPayment.status);
    const endpoint = isPrePay
      ? `${API_URL}/orders/${selectedOrderForPayment._id}/prepay`
      : `${API_URL}/orders/${selectedOrderForPayment._id}/complete-payment`;

    setProcessingPayment(true);
    try {
      const response = await axios.post(endpoint, {
        paymentMethod,
        amountReceived: paymentMethod === 'cash' ? (parseFloat(amountReceived) || 0) : null,
        customerId: paymentCustomer?._id,
        newCustomer: showNewCustomerInPayment ? newPaymentCustomer : null,
        keepChange: balanceToSave > 0,
        saveBalanceAmount: balanceToSave,
        useBalance: useBalance ? balanceToUse : 0,
        usePoints: usePoints > 0 ? usePoints : 0,
        bankAccount: paymentMethod === 'transfer' && selectedBankAccount ? {
          bankName: selectedBankAccount.bankName,
          accountNumber: selectedBankAccount.accountNumber,
          accountName: selectedBankAccount.accountName
        } : null
      });
      
      setShowPaymentModal(false);
      setSelectedOrderForPayment(null);
      
      // Refresh customers if balance/points changed or new one was created
      if (response.data.customer || balanceToSave > 0 || useBalance || usePoints > 0) {
        loadCustomers();
      }
      
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xử lý thanh toán thất bại');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Create order functions
  const openCreateOrderModal = () => {
    // Default delivery date = today (YYYY-MM-DD in local timezone)
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    setCreateOrderForm({
      tableId: '',
      items: [],
      customerNote: '',
      customerId: '',
      deliveryDate: todayStr,
      deliveryTime: ''
    });
    setMenuSearchTerm('');
    setSelectedMenuCategory('all');
    setSelectedCustomerDiscount(null);
    setDisplayedItemsCount(20); // Reset scroll load
    setConfiguringItem(null);
    setItemOptions({
      temperature: 'Mặc định',
      sweetness: 'Mặc định',
      selectedOption: null,
      selectedToppings: [],
      quantity: 1
    });
    setShowNewCustomerInOrder(false);
    setNewCustomerForm({ name: '', phone: '' });
    setCreateOrderCustomerSearch('');
    setShowCustomerSearchDropdown(false);
    setCreateOrderCustomerResults([]);
    // Load initial customers
    searchCreateOrderCustomers('');
    // Load active discounts
    loadActiveDiscounts();
    setShowCreateOrderModal(true);
  };

  // Create new customer from create order modal
  const createNewCustomerInOrder = async () => {
    if (!newCustomerForm.name.trim()) {
      toast.error('Vui lòng nhập tên khách hàng');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/customers`, {
        name: newCustomerForm.name.trim(),
        phone: newCustomerForm.phone.trim() || null
      });
      const newCustomer = response.data.data;
      // Add to customers list and select it
      setCustomers(prev => [newCustomer, ...prev]);
      setCreateOrderForm(prev => ({ ...prev, customerId: newCustomer._id }));
      setShowNewCustomerInOrder(false);
      setNewCustomerForm({ name: '', phone: '' });
      setCreateOrderCustomerSearch(newCustomer.name);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể tạo khách hàng');
    }
  };

  // Filter customers for search in create order
  // Search customers for create order - call API
  const searchCreateOrderCustomers = async (search) => {
    setCreateOrderCustomerSearch(search);
    if (search.length >= 1) {
      try {
        const response = await axios.get(`${API_URL}/customers`, {
          params: { search, limit: 20 }
        });
        setCreateOrderCustomerResults(response.data.data || []);
      } catch (error) {
        console.error('Error searching customers:', error);
        setCreateOrderCustomerResults([]);
      }
    } else {
      // Load recent customers when no search term
      try {
        const response = await axios.get(`${API_URL}/customers`, {
          params: { limit: 20 }
        });
        setCreateOrderCustomerResults(response.data.data || []);
      } catch (error) {
        setCreateOrderCustomerResults([]);
      }
    }
  };

  // Get filtered customers for order (use search results)
  const getFilteredCustomersForOrder = () => {
    return createOrderCustomerResults;
  };

  // Select customer in create order
  const selectCustomerForOrder = async (customer) => {
    setCreateOrderForm(prev => ({ ...prev, customerId: customer._id }));
    setCreateOrderCustomerSearch(customer.name + (customer.phone ? ` - ${customer.phone}` : ''));
    setShowCustomerSearchDropdown(false);
    
    // Fetch customer discount
    try {
      const response = await axios.get(`${API_URL}/discounts/customer/${customer._id}`);
      if (response.data.success && response.data.data.length > 0) {
        // Find active discount
        const now = new Date();
        const activeDiscount = response.data.data.find(d => {
          if (!d.isActive) return false;
          // Check startDate (if not set, consider it valid)
          if (d.startDate && new Date(d.startDate) > now) return false;
          // Check endDate (if not set, consider it valid)
          if (d.endDate && new Date(d.endDate) < now) return false;
          return true;
        });
        setSelectedCustomerDiscount(activeDiscount || null);
      } else {
        setSelectedCustomerDiscount(null);
      }
    } catch (error) {
      console.error('Error fetching customer discount:', error);
      setSelectedCustomerDiscount(null);
    }
  };

  // Clear selected customer
  const clearSelectedCustomer = () => {
    setCreateOrderForm(prev => ({ ...prev, customerId: '' }));
    setCreateOrderCustomerSearch('');
    setSelectedCustomerDiscount(null);
  };

  // Load active discounts for create order modal
  const loadActiveDiscounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/discounts`);
      if (response.data.success) {
        const now = new Date();
        // Filter active discounts
        const active = response.data.data.filter(d => {
          if (!d.isActive) return false;
          if (d.startDate && new Date(d.startDate) > now) return false;
          if (d.endDate && new Date(d.endDate) < now) return false;
          return true;
        });
        setActiveDiscounts(active);
      }
    } catch (error) {
      console.error('Error loading discounts:', error);
      setActiveDiscounts([]);
    }
  };

  // When clicking on a menu item, open configuration panel
  const selectItemToConfigure = (item) => {
    setConfiguringItem(item);
    setItemOptions({
      temperature: 'Mặc định',
      sweetness: 'Mặc định',
      selectedOption: item.customOptions?.length > 0 ? item.customOptions[0] : null,
      selectedToppings: [],
      quantity: 1
    });
  };

  const toggleTopping = (topping) => {
    setItemOptions(prev => {
      const exists = prev.selectedToppings.find(t => t._id === topping._id);
      if (exists) {
        return { ...prev, selectedToppings: prev.selectedToppings.filter(t => t._id !== topping._id) };
      }
      return { ...prev, selectedToppings: [...prev.selectedToppings, topping] };
    });
  };

  const getItemConfigPrice = () => {
    if (!configuringItem) return 0;
    const basePrice = configuringItem.price;
    const optionPrice = itemOptions.selectedOption?.price || 0;
    const toppingPrice = itemOptions.selectedToppings.reduce((sum, t) => sum + (t.price || 0), 0);
    return (basePrice + optionPrice + toppingPrice) * itemOptions.quantity;
  };

  const addConfiguredItemToOrder = () => {
    if (!configuringItem) return;
    
    const basePrice = configuringItem.price;
    const optionPrice = itemOptions.selectedOption?.price || 0;
    const toppingPrice = itemOptions.selectedToppings.reduce((sum, t) => sum + (t.price || 0), 0);
    const finalPrice = basePrice + optionPrice + toppingPrice;

    const newItem = {
      menuItem: configuringItem._id,
      name: configuringItem.name,
      price: finalPrice,
      originalPrice: finalPrice, // Lưu giá gốc để so sánh khi sửa giá
      quantity: itemOptions.quantity,
      temperature: itemOptions.temperature,
      sweetness: itemOptions.sweetness,
      selectedOption: itemOptions.selectedOption,
      selectedToppings: itemOptions.selectedToppings
    };

    setCreateOrderForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    // Reset configuration
    setConfiguringItem(null);
    setItemOptions({
      temperature: 'Mặc định',
      sweetness: 'Mặc định',
      selectedOption: null,
      selectedToppings: [],
      quantity: 1
    });
  };

  const updateCreateOrderItemQty = (index, value, isDirect = false) => {
    setCreateOrderForm(prev => {
      const newItems = [...prev.items];
      if (isDirect) {
        // Nhập trực tiếp số lượng
        const newQty = parseInt(value) || 1;
        newItems[index].quantity = Math.max(1, newQty);
      } else {
        // Tăng/giảm bằng nút +/-
        newItems[index].quantity = Math.max(1, newItems[index].quantity + value);
      }
      return { ...prev, items: newItems };
    });
  };

  const updateCreateOrderItemPrice = (index, newPrice) => {
    setCreateOrderForm(prev => {
      const newItems = [...prev.items];
      const price = parseFloat(newPrice) || 0;
      // Lưu giá gốc nếu chưa có (để hiển thị khi giá bị sửa)
      // Use undefined check instead of falsy check to allow originalPrice = 0
      if (newItems[index].originalPrice === undefined) {
        newItems[index].originalPrice = newItems[index].price;
      }
      newItems[index].price = Math.max(0, price);
      return { ...prev, items: newItems };
    });
  };

  const removeCreateOrderItem = (index) => {
    setCreateOrderForm(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const getCreateOrderTotal = () => {
    const subtotal = createOrderForm.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    return subtotal;
  };

  const getCreateOrderDiscount = () => {
    if (!selectedCustomerDiscount) return 0;
    const subtotal = getCreateOrderTotal();
    
    // Calculate discount and round to nearest 1000 (same as customer side)
    const discountAmount = subtotal * selectedCustomerDiscount.percentage / 100;
    return Math.round(discountAmount / 1000) * 1000;
  };

  const getCreateOrderExtraFee = () => {
    return createOrderForm.extraFee?.amount || 0;
  };

  // Calculate manual discount amount for desktop
  const getDesktopManualDiscountAmount = () => {
    const value = parseFloat(desktopManualDiscountValue) || 0;
    if (value <= 0) return 0;
    
    const subtotal = getCreateOrderTotal();
    if (desktopManualDiscountType === 'percent') {
      return Math.round(subtotal * value / 100);
    }
    return value;
  };

  const getCreateOrderFinalTotal = () => {
    return getCreateOrderTotal() - getCreateOrderDiscount() - getDesktopManualDiscountAmount() + getCreateOrderExtraFee();
  };

  const submitCreateOrder = async () => {
    if (createOrderForm.items.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 món');
      return;
    }

    try {
      const selectedTableData = createOrderForm.tableId 
        ? tables.find(t => t._id === createOrderForm.tableId) 
        : null;
      
      // Get current user info for soldBy
      const soldBy = {
        odId: localStorage.getItem('adminId') || null,
        username: localStorage.getItem('userName') || 'admin',
        role: localStorage.getItem('userRole') || 'admin'
      };
      
      // Prepare extraFee if has amount
      let extraFeeData = null;
      if (createOrderForm.extraFee && createOrderForm.extraFee.amount > 0) {
        extraFeeData = {
          amount: createOrderForm.extraFee.amount,
          description: createOrderForm.extraFee.description || '',
          excludeFromRevenue: createOrderForm.extraFee.excludeFromRevenue || false,
          expenseGroup: createOrderForm.extraFee.expenseGroup || null,
          expenseGroupName: createOrderForm.extraFee.expenseGroupName || '',
          recipient: createOrderForm.extraFee.recipient || ''
        };
      }
      
      // Prepare manualDiscount - check both desktop state and createOrderForm (for mobile)
      let manualDiscountData = null;
      const desktopManualAmount = getDesktopManualDiscountAmount();
      if (desktopManualAmount > 0) {
        // Desktop manual discount
        manualDiscountData = {
          type: desktopManualDiscountType,
          value: parseFloat(desktopManualDiscountValue) || 0,
          amount: desktopManualAmount
        };
      } else if (createOrderForm.manualDiscount && createOrderForm.manualDiscount.amount > 0) {
        // Mobile manual discount (passed via createOrderForm)
        manualDiscountData = {
          type: createOrderForm.manualDiscount.type,
          value: createOrderForm.manualDiscount.value,
          amount: createOrderForm.manualDiscount.amount
        };
      }
      
      // Build deliveryAt ISO string if both date and time provided
      let deliveryAtIso = null;
      if (createOrderForm.deliveryDate && createOrderForm.deliveryTime) {
        // Combine into local datetime then to ISO
        const local = new Date(`${createOrderForm.deliveryDate}T${createOrderForm.deliveryTime}:00`);
        if (!isNaN(local.getTime())) {
          deliveryAtIso = local.toISOString();
        }
      }

      await axios.post(`${API_URL}/orders/admin-create`, {
        tableNumber: selectedTableData?.tableNumber || null,
        items: createOrderForm.items,
        customerNote: createOrderForm.customerNote,
        customerId: createOrderForm.customerId || null,
        discountId: selectedCustomerDiscount?._id || null,
        soldBy,
        extraFee: extraFeeData,
        manualDiscount: manualDiscountData,
        deliveryAt: deliveryAtIso
      });
      // Removed: toast.success('Đã tạo đơn hàng');
      setShowCreateOrderModal(false);
      // Reset form including extraFee
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      setCreateOrderForm({
        tableId: '',
        items: [],
        customerNote: '',
        customerId: '',
        deliveryDate: todayStr,
        deliveryTime: '',
        extraFee: {
          amount: 0,
          description: '',
          excludeFromRevenue: false,
          expenseGroup: '',
          expenseGroupName: '',
          recipient: ''
        },
        manualDiscount: null
      });
      setSelectedCustomerDiscount(null);
      // Reset desktop manual discount state
      setDesktopManualDiscountType('fixed');
      setDesktopManualDiscountValue('');
      await loadOrders();
      setFilter('preparing');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể tạo đơn hàng');
    }
  };

  const getFilteredMenuItems = () => {
    let filtered = menuItems.filter(item => item.isAvailable !== false);
    
    if (selectedMenuCategory && selectedMenuCategory !== 'all') {
      filtered = filtered.filter(item => {
        // item.category là string (tên danh mục)
        return item.category === selectedMenuCategory;
      });
    }
    
    if (menuSearchTerm) {
      const searchLower = menuSearchTerm.toLowerCase();
      const searchNormalized = removeVietnameseDiacritics(menuSearchTerm);
      
      filtered = filtered.filter(item => {
        const nameLower = (item.name || '').toLowerCase();
        const nameNormalized = removeVietnameseDiacritics(item.name);
        
        // Match if: exact match OR normalized match (diacritics-insensitive)
        return nameLower.includes(searchLower) || nameNormalized.includes(searchNormalized);
      });
    }
    
    return filtered;
  };

  // Get displayed items with scroll load limit
  const getDisplayedMenuItems = () => {
    const filtered = getFilteredMenuItems();
    return filtered.slice(0, displayedItemsCount);
  };

  // Handle scroll to load more items
  const handleMenuScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when scrolled near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const filtered = getFilteredMenuItems();
      if (displayedItemsCount < filtered.length) {
        setDisplayedItemsCount(prev => Math.min(prev + 20, filtered.length));
      }
    }
  };

  // Reset displayed count when filter/search changes (debounced to prevent focus loss)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayedItemsCount(20);
    }, 300); // Debounce 300ms
    return () => clearTimeout(timer);
  }, [menuSearchTerm, selectedMenuCategory]);

  const cancelOrder = async (orderId, tableNumber) => {
    if (window.confirm(`Hủy đơn hàng của bàn ${tableNumber}?\n\nĐơn hàng sẽ bị xóa hoàn toàn.`)) {
      try {
        const response = await axios.delete(`${API_URL}/orders/${orderId}`);
        
        // Log restore results for debugging
        if (response.data?.ingredientRestoreResults) {
          const results = response.data.ingredientRestoreResults;
          console.log('Ingredient restore results:', results);
          
          if (results.errors && results.errors.length > 0) {
            console.warn('Restore errors:', results.errors);
          }
          
          if (results.restoredIngredients && results.restoredIngredients.length > 0) {
            console.log('Restored ingredients:', results.restoredIngredients);
            toast.success(`Đã hoàn ${results.restoredIngredients.length} nguyên liệu`);
          } else if (results.menuItemIds && results.menuItemIds.length > 0 && results.recipesFound.length === 0) {
            console.warn('No recipes found for menu items:', results.menuItemIds);
          }
        }
        
        loadOrders();
      } catch (error) {
        toast.error('Hủy đơn hàng thất bại');
      }
    }
  };

  // Update item quantity in order
  const updateItemQuantity = async (orderId, itemIndex, newQuantity) => {
    // Update local state instead of calling API directly
    setEditingOrderItems(prev => {
      const updated = [...prev];
      if (newQuantity <= 0) {
        updated.splice(itemIndex, 1);
      } else {
        updated[itemIndex] = { ...updated[itemIndex], quantity: newQuantity };
      }
      return updated;
    });
  };

  // Update item price when editing
  const updateItemPrice = (orderId, itemIndex, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    setEditingOrderItems(prev => {
      const updated = [...prev];
      updated[itemIndex] = { 
        ...updated[itemIndex], 
        price: price,
        priceEdited: true // Mark as edited
      };
      return updated;
    });
  };

  // Load recipe for a menu item (for viewing preparation steps)
  const loadRecipeForItem = async (item) => {
    if (!item.menuItem) return;
    
    const menuItemId = item.menuItem._id || item.menuItem;
    const sizeOption = item.selectedOption?.name || null;
    
    setLoadingRecipe(true);
    setShowRecipeModal(true);
    
    try {
      // Fetch recipe for this menu item and size option
      const response = await axios.get(`${API_URL}/recipes`, {
        params: {
          type: 'dish',
          menuItemId: menuItemId
        }
      });
      
      if (response.data.success && response.data.data) {
        const recipes = response.data.data;
        
        // Find matching recipe - prioritize size-specific, fallback to default
        let recipe = null;
        
        if (sizeOption) {
          // Look for size-specific recipe first
          recipe = recipes.find(r => r.sizeOption === sizeOption);
        }
        
        // Fallback to default recipe (no sizeOption)
        if (!recipe) {
          recipe = recipes.find(r => !r.sizeOption || r.sizeOption === null);
        }
        
        // If still no recipe, use first one found
        if (!recipe && recipes.length > 0) {
          recipe = recipes[0];
        }
        
        if (recipe) {
          setSelectedRecipe({
            ...recipe,
            itemName: item.name,
            sizeOption: sizeOption
          });
        } else {
          setShowRecipeModal(false);
          toast.info('Không tìm thấy công thức cho món này');
        }
      } else {
        setShowRecipeModal(false);
        toast.info('Không tìm thấy công thức cho món này');
      }
    } catch (error) {
      console.error('Error loading recipe:', error);
      setShowRecipeModal(false);
      toast.error('Không thể tải công thức');
    } finally {
      setLoadingRecipe(false);
    }
  };

  // Check if an item has recipe
  const hasRecipe = (item) => {
    if (!item.menuItem) return false;
    
    // Get menuItemId and convert to string for comparison
    let menuItemId = item.menuItem._id || item.menuItem;
    if (menuItemId && typeof menuItemId === 'object') {
      menuItemId = menuItemId.toString();
    } else if (menuItemId) {
      menuItemId = menuItemId.toString();
    }
    
    if (!menuItemId) return false;
    
    const sizeOption = item.selectedOption?.name || null;
    
    // Check for size-specific recipe
    if (sizeOption) {
      const sizeKey = `${menuItemId}_${sizeOption}`;
      if (recipesWithSteps[sizeKey]) return true;
    }
    
    // Check for default recipe
    const defaultKey = `${menuItemId}_default`;
    const fallbackKey = `${menuItemId}_fallback`;
    
    return recipesWithSteps[defaultKey] || recipesWithSteps[fallbackKey] || false;
  };

  // Start editing an order - copy items to local state
  const startEditingOrder = (order) => {
    setEditingOrderId(order._id);
    setEditingOrderItems(order.items.map(item => ({ ...item })));
    setEditingOrderDiscount(order.discount ? { 
      _id: order.discount._id || 'existing',
      name: order.discount.name,
      percentage: order.discount.percentage
    } : null);
    setEditingOrderExtraFee(order.extraFee ? {
      amount: order.extraFee.amount || 0,
      description: order.extraFee.description || '',
      excludeFromRevenue: order.extraFee.excludeFromRevenue || false,
      expenseGroup: order.extraFee.expenseGroup || '',
      expenseGroupName: order.extraFee.expenseGroupName || '',
      recipient: order.extraFee.recipient || ''
    } : { amount: 0, description: '', excludeFromRevenue: false, expenseGroup: '', expenseGroupName: '', recipient: '' });
    // Load manual discount if exists
    setEditingOrderManualDiscount(order.manualDiscount ? {
      type: order.manualDiscount.discountType || 'fixed',
      value: order.manualDiscount.value?.toString() || '',
      amount: order.manualDiscount.amount || 0
    } : { type: 'fixed', value: '', amount: 0 });
    // Load active discounts for selection
    loadActiveDiscounts();
  };

  // Cancel editing - restore original state
  const cancelEditingOrder = () => {
    setEditingOrderId(null);
    setEditingOrderItems([]);
    setEditingOrderDiscount(null);
    setEditingOrderExtraFee({ amount: 0, description: '', excludeFromRevenue: false, expenseGroup: '', expenseGroupName: '', recipient: '' });
    setEditingOrderManualDiscount({ type: 'fixed', value: '', amount: 0 });
  };

  // Save edited order
  const saveEditedOrder = async (orderId) => {
    try {
      // Check if order would be empty
      if (editingOrderItems.length === 0) {
        if (window.confirm('Đơn hàng sẽ trống. Bạn có muốn xóa đơn hàng không?')) {
          await axios.delete(`${API_URL}/orders/${orderId}`);
          loadOrders();
        }
        cancelEditingOrder();
        return;
      }

      // Determine discountId to send
      let discountId;
      if (editingOrderDiscount === null) {
        discountId = null; // Explicitly remove discount
      } else if (editingOrderDiscount?._id && editingOrderDiscount._id !== 'existing') {
        discountId = editingOrderDiscount._id; // Apply new discount
      }
      // If discountId is undefined, API will recalculate existing discount

      // Prepare extraFee data
      const extraFeeData = editingOrderExtraFee.amount > 0 ? {
        amount: editingOrderExtraFee.amount,
        description: editingOrderExtraFee.description || '',
        excludeFromRevenue: editingOrderExtraFee.excludeFromRevenue || false,
        expenseGroup: editingOrderExtraFee.expenseGroup || null,
        expenseGroupName: editingOrderExtraFee.expenseGroupName || '',
        recipient: editingOrderExtraFee.recipient || ''
      } : null;

      // Prepare manualDiscount data
      const manualDiscountAmount = getEditingOrderManualDiscountAmount();
      const manualDiscountData = manualDiscountAmount > 0 ? {
        type: editingOrderManualDiscount.type,
        value: parseFloat(editingOrderManualDiscount.value) || 0,
        amount: manualDiscountAmount
      } : null;

      await axios.put(`${API_URL}/orders/${orderId}/items`, { 
        items: editingOrderItems,
        discountId,
        extraFee: extraFeeData,
        manualDiscount: manualDiscountData
      });
      
      cancelEditingOrder();
      loadOrders();
    } catch (error) {
      toast.error('Không thể cập nhật đơn hàng');
    }
  };

  // Calculate subtotal for editing order
  const getEditingOrderSubtotal = () => {
    return editingOrderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  // Calculate discount amount for editing order (round to nearest 1000 - same as customer side)
  const getEditingOrderDiscountAmount = () => {
    if (!editingOrderDiscount) return 0;
    const subtotal = getEditingOrderSubtotal();
    const discountAmount = subtotal * editingOrderDiscount.percentage / 100;
    return Math.round(discountAmount / 1000) * 1000;
  };

  // Calculate extra fee for editing order
  const getEditingOrderExtraFee = () => {
    return editingOrderExtraFee?.amount || 0;
  };

  // Calculate manual discount amount for editing order
  const getEditingOrderManualDiscountAmount = () => {
    const value = parseFloat(editingOrderManualDiscount.value) || 0;
    if (value <= 0) return 0;
    
    const subtotal = getEditingOrderSubtotal();
    if (editingOrderManualDiscount.type === 'percent') {
      return Math.round(subtotal * value / 100);
    }
    return value;
  };

  // Calculate total for editing order
  const getEditingOrderTotal = () => {
    return Math.max(0, getEditingOrderSubtotal() - getEditingOrderDiscountAmount() - getEditingOrderManualDiscountAmount() + getEditingOrderExtraFee());
  };

  // Delete item from order (local state update)
  const deleteItemFromOrder = (orderId, itemIndex) => {
    if (!window.confirm('Xóa món này khỏi đơn hàng?')) return;
    setEditingOrderItems(prev => prev.filter((_, idx) => idx !== itemIndex));
  };

  // Open delete order modal
  const openDeleteOrderModal = (order) => {
    setOrderToDelete(order);
    setDeletePassword('');
    setShowDeleteOrderModal(true);
  };

  // Delete order with password verification
  const handleDeleteOrderWithPassword = async () => {
    if (!deletePassword) {
      toast.error('Vui lòng nhập mật khẩu');
      return;
    }

    try {
      // Verify delete password (using DELETE_ORDERS_PASSWORD from .env)
      await axios.post(`${API_URL}/orders/verify-delete-password`, {
        password: deletePassword
      });

      // Password correct, delete order
      const response = await axios.delete(`${API_URL}/orders/${orderToDelete._id}`);
      
      // Log restore results for debugging
      if (response.data?.ingredientRestoreResults) {
        const results = response.data.ingredientRestoreResults;
        console.log('Ingredient restore results:', results);
        
        if (results.errors && results.errors.length > 0) {
          console.warn('Restore errors:', results.errors);
        }
        
        if (results.restoredIngredients && results.restoredIngredients.length > 0) {
          console.log('Restored ingredients:', results.restoredIngredients);
          toast.success(`Đã hoàn ${results.restoredIngredients.length} nguyên liệu`);
        }
      }
      
      setShowDeleteOrderModal(false);
      setOrderToDelete(null);
      setDeletePassword('');
      loadOrders();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Mật khẩu không đúng');
      } else {
        toast.error('Không thể xóa đơn hàng');
      }
    }
  };

  // Helper function to check if item should show ice/sweetness/topping options
  const getItemOptions = (item) => {
    // Categories that should NOT show ice options
    const noIceCategories = ['đá xay', 'sinh tố', 'sữa chua', 'đồ ăn vặt', 'khác'];
    
    // Categories that should NOT show sweetness options
    const noSweetnessCategories = ['đồ ăn vặt', 'khác'];
    
    // Categories that should NOT show topping options
    const noToppingCategories = ['đồ ăn vặt', 'khác'];
    
    // Categories that are drinks (show options)
    const drinkCategories = ['cafe', 'cà phê', 'trà sữa', 'trà trái cây', 'cacao', 
      'nước ép', 'đá xay', 'sinh tố', 'sữa chua', 'sữa tươi', 'matcha', 'soda', 'đồ uống khác'];
    
    const categoryLower = (item?.category || '').toLowerCase();
    
    // Check if it's a drink category
    const isDrink = drinkCategories.some(cat => categoryLower.includes(cat));
    
    // Check specific options availability
    const showIceOptions = isDrink && !noIceCategories.some(cat => categoryLower.includes(cat));
    const showSweetnessOptions = isDrink && !noSweetnessCategories.some(cat => categoryLower.includes(cat));
    const showToppingOptions = !noToppingCategories.some(cat => categoryLower.includes(cat));
    
    return { isDrink, showIceOptions, showSweetnessOptions, showToppingOptions };
  };

  // Open add item modal
  const openAddItemModal = (order) => {
    setOrderToAddItem(order);
    setAddItemSearch('');
    setAddItemCategory('all');
    setAddItemConfiguring(null);
    setAddItemOptions({
      temperature: 'Mặc định',
      sweetness: 'Mặc định',
      selectedOption: null,
      selectedToppings: [],
      quantity: 1
    });
    setShowAddItemModal(true);
    // Load menu and toppings if not loaded
    if (menuItems.length === 0) loadMenuItems();
    if (categories.length === 0) loadCategories();
    if (toppings.length === 0) loadToppings();
  };

  // Select item to configure in add item modal
  const selectAddItem = (item) => {
    setAddItemConfiguring(item);
    setAddItemOptions({
      temperature: 'Mặc định',
      sweetness: 'Mặc định',
      selectedOption: item.customOptions?.length > 0 ? item.customOptions[0] : null,
      selectedToppings: [],
      quantity: 1
    });
  };

  // Toggle topping in add item modal
  const toggleAddItemTopping = (topping) => {
    const exists = addItemOptions.selectedToppings.some(t => t._id === topping._id);
    if (exists) {
      setAddItemOptions({
        ...addItemOptions,
        selectedToppings: addItemOptions.selectedToppings.filter(t => t._id !== topping._id)
      });
    } else {
      setAddItemOptions({
        ...addItemOptions,
        selectedToppings: [...addItemOptions.selectedToppings, topping]
      });
    }
  };

  // Calculate price for add item
  const getAddItemPrice = () => {
    if (!addItemConfiguring) return 0;
    let price = addItemConfiguring.price;
    if (addItemOptions.selectedOption?.price) {
      price += addItemOptions.selectedOption.price;
    }
    addItemOptions.selectedToppings.forEach(t => {
      price += t.price;
    });
    return price * addItemOptions.quantity;
  };

  // Add item to existing order
  const addItemToExistingOrder = async () => {
    if (!addItemConfiguring || !orderToAddItem) return;

    const newItem = {
      menuItem: addItemConfiguring._id,
      name: addItemConfiguring.name,
      category: addItemConfiguring.category, // Add category for discount calculation
      price: addItemConfiguring.price + (addItemOptions.selectedOption?.price || 0) + 
             addItemOptions.selectedToppings.reduce((sum, t) => sum + t.price, 0),
      quantity: addItemOptions.quantity,
      temperature: addItemOptions.temperature,
      sweetness: addItemOptions.sweetness,
      selectedOption: addItemOptions.selectedOption,
      selectedToppings: addItemOptions.selectedToppings.map(t => ({
        _id: t._id,
        name: t.name,
        price: t.price
      }))
    };

    // Add to local editing state
    setEditingOrderItems(prev => [...prev, newItem]);
    
    setShowAddItemModal(false);
    setOrderToAddItem(null);
    setAddItemConfiguring(null);
    toast.success('Đã thêm món vào đơn hàng');
  };

  // Filter menu items for add item modal
  const getFilteredAddItems = () => {
    let filtered = menuItems.filter(item => item.isAvailable !== false);
    
    if (addItemCategory && addItemCategory !== 'all') {
      filtered = filtered.filter(item => item.category === addItemCategory);
    }
    
    if (addItemSearch) {
      const searchNormalized = removeVietnameseDiacritics(addItemSearch);
      filtered = filtered.filter(item => {
        const nameNormalized = removeVietnameseDiacritics(item.name);
        return nameNormalized.includes(searchNormalized) || 
               item.name.toLowerCase().includes(addItemSearch.toLowerCase());
      });
    }
    
    return filtered;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Math.round(amount || 0));
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      paid: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    
    const labels = {
      pending: 'Chờ xác nhận',
      preparing: 'Đang làm',
      completed: 'Hoàn thành',
      paid: 'Đã thanh toán',
      cancelled: 'Đã hủy'
    };

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  // Filter orders based on selected filters (for non-paid orders)
  const getFilteredOrders = () => {
    let filtered = orders;

    // Filter by table for "completed" tab
    if (filter === 'completed' && selectedTable !== 'all') {
      filtered = filtered.filter(order => order.tableNumber === selectedTable);
    }

    // For paid tab, use paidOrders state (already filtered from server)
    if (filter === 'paid') {
      return paidOrders;
    }

    return filtered;
  };

  // Use total revenue from server for paid orders
  const filteredPaidRevenue = filter === 'paid' ? paidTotalRevenue : 0;

  const filteredOrders = getFilteredOrders();

  // Reset filters when changing tabs
  const handleFilterChange = async (newFilter) => {
    setFilter(newFilter);
    setSelectedTable('all');
    setSelectedDate('');
    setPaidPaymentFilter('all');
    setPaidDateFrom('');
    setPaidDateTo('');
    setPaidSearchTerm('');
    setDebouncedPaidSearch('');
    setPaidOrdersPage(1);
    // Reset editing state
    setEditingOrderId(null);
    setEditingOrderItems([]);
    setEditingOrderDiscount(null);
    setPaidOrders([]);
    setPaidOrdersTotal(0);
    setPaidTotalRevenue(0);
    setPaidCashTotal(0);
    setPaidTransferTotal(0);
    setPaidDebtTotal(0);
    setPaidPointsDiscountTotal(0);
    setHasMorePaidOrders(false);
    
    // Load paid orders when switching to paid tab
    if (newFilter === 'paid') {
      setLoading(true);
      try {
        const params = {
          status: 'paid',
          page: 1,
          limit: paidOrdersPerPage
        };
        // Send default bank account number for filtering logic
        if (selectedBankAccount?.accountNumber) {
          params.defaultBankAccountNumber = selectedBankAccount.accountNumber;
        }
        const config = getAuthConfig();
        const response = await axios.get(`${API_URL}/orders`, { params, ...config });
        const { data, pagination } = response.data;
        setPaidOrders(data);
        setPaidOrdersTotal(pagination?.total || 0);
        setPaidTotalRevenue(pagination?.totalRevenue || 0);
        setPaidCashTotal(pagination?.cashTotal || 0);
        setPaidTransferTotal(pagination?.transferTotal || 0);
        setPaidDebtTotal(pagination?.debtTotal || 0);
        setPaidPointsDiscountTotal(pagination?.pointsDiscountTotal || 0);
        setHasMorePaidOrders(pagination?.hasMore || false);
      } catch (error) {
        toast.error('Không thể tải danh sách đơn hàng đã thanh toán');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle scroll load more for paid orders (server-side)
  const handlePaidOrdersScroll = async (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMorePaidOrders && !loadingMorePaid) {
      const nextPage = paidOrdersPage + 1;
      await loadPaidOrders(nextPage, false);
    }
  };

  // Reload paid orders when filters change
  const handlePaidFilterChange = async () => {
    setPaidOrdersPage(1);
    setSelectedPaidOrders([]);
    await loadPaidOrders(1, true);
  };

  // Xác nhận thanh toán khi có nhiều đơn cùng số tiền
  const confirmPendingPayment = async () => {
    if (!selectedPendingOrderId || !pendingPaymentData) {
      toast.error('Vui lòng chọn đơn hàng');
      return;
    }
    
    setConfirmingPayment(true);
    try {
      const response = await axios.post(`${API_URL}/payment-webhook/confirm-payment`, {
        orderId: selectedPendingOrderId,
        transactionId: pendingPaymentData.transactionId,
        amount: pendingPaymentData.amount,
        content: pendingPaymentData.content,
        referenceCode: pendingPaymentData.referenceCode,
        transactionDate: pendingPaymentData.transactionDate,
        bankInfo: pendingPaymentData.bankInfo
      });
      
      if (response.data.success) {
        toast.success('✅ Đã xác nhận thanh toán thành công');
        setShowPaymentSelectionModal(false);
        setPendingPaymentData(null);
        setSelectedPendingOrderId(null);
        loadOrders(); // Refresh danh sách
      } else {
        toast.error(response.data.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      toast.error(error.response?.data?.message || 'Không thể xác nhận thanh toán');
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Long press handlers for paid orders
  const handlePaidTouchStart = (e, orderId) => {
    paidIsLongPress.current = false;
    paidLongPressTimer.current = setTimeout(() => {
      paidIsLongPress.current = true;
      setPaidBulkSelectMode(true);
      toggleSelectPaidOrder(orderId);
      if (navigator.vibrate) navigator.vibrate(50);
    }, 800);
  };

  const handlePaidTouchEnd = () => {
    if (paidLongPressTimer.current) {
      clearTimeout(paidLongPressTimer.current);
      paidLongPressTimer.current = null;
    }
  };

  const handlePaidTouchMove = () => {
    if (paidLongPressTimer.current) {
      clearTimeout(paidLongPressTimer.current);
      paidLongPressTimer.current = null;
    }
  };

  // Mobile Paid Order Card with long press
  const MobilePaidOrderCard = ({ order }) => {
    const isSelected = selectedPaidOrders.includes(order._id);
    
    return (
      <div 
        className={`bg-white p-3 active:bg-gray-50 ${isSelected ? 'bg-red-50' : ''}`}
        onTouchStart={(e) => handlePaidTouchStart(e, order._id)}
        onTouchEnd={handlePaidTouchEnd}
        onTouchMove={handlePaidTouchMove}
        onClick={() => {
          if (paidIsLongPress.current) return;
          if (paidBulkSelectMode) {
            toggleSelectPaidOrder(order._id);
          } else {
            setSelectedOrderDetail(order);
            setShowOrderDetailModal(true);
          }
        }}
      >
        <div className="flex items-start">
          {/* Checkbox for bulk select */}
          {paidBulkSelectMode && (
            <div className="mr-3 pt-1">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300 bg-white'}`}>
                {isSelected && <FiCheck className="text-white" size={12} />}
              </div>
            </div>
          )}
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Table/Customer & Amount */}
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-sm text-gray-900">
                  {order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi')}
                </span>
                {(order.orderCode || order.importCode) && (
                  <span className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                    {order.orderCode || order.importCode}
                  </span>
                )}
                {order.customer?.name && (
                  <span className="text-xs text-gray-500">• {order.customer.name}</span>
                )}
              </div>
              <div className="text-right">
                {order.pointsDiscount > 0 && (
                  <span className="text-xs text-gray-400 line-through mr-1">{formatCurrency(order.totalAmount)}</span>
                )}
                <span className="font-bold text-sm text-green-600">
                  {formatCurrency(order.totalAmount - (order.pointsDiscount || 0))}
                </span>
              </div>
            </div>
            
            {/* Row 2: Items summary */}
            <p className="text-xs text-gray-500 mb-1 truncate">
              {order.items.map((item, idx) => (
                <span key={idx}>
                  {item.name} x{item.quantity}{idx < order.items.length - 1 ? ', ' : ''}
                </span>
              ))}
            </p>
            
            {/* Row 3: Time & Payment method */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {new Date(order.createdAt).toLocaleString('vi-VN', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                })}
              </span>
              <div className="flex items-center space-x-2">
                {order.pointsUsed > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    -{order.pointsUsed}đ
                  </span>
                )}
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                  order.paymentMethod === 'transfer' ? 'bg-blue-100 text-blue-700' : 
                  order.paymentMethod === 'debt' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                }`}>
                  {order.paymentMethod === 'transfer' ? 'CK' : order.paymentMethod === 'debt' ? 'Nợ' : 'TM'}
                </span>
                {order.debtAmount > 0 && order.paymentMethod !== 'debt' && (
                  <span className="text-xs text-orange-600">+Nợ</span>
                )}
                {!paidBulkSelectMode && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedOrderForShare(order); setShowShareModal(true); }}
                      className="p-1 text-gray-400"
                      title="Chia sẻ"
                    >
                      <FiShare2 size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handlePrint(order); }}
                      className="p-1 text-gray-400"
                    >
                      <FiPrinter size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          
          {/* Arrow */}
          {!paidBulkSelectMode && <FiChevronRight className="text-gray-300 ml-2 mt-1" size={16} />}
        </div>
      </div>
    );
  };

  return (
    <div className={isMobile ? "-m-4 bg-gray-50 pb-4" : "space-y-6"}>
      {/* Header */}
      <div className={isMobile ? "bg-white px-4 py-3 flex items-center justify-between shadow-sm" : "flex items-center justify-between"}>
        <h1 className={isMobile ? "text-lg font-bold text-gray-800" : "text-3xl font-bold text-gray-800"}>Quản lý đơn hàng</h1>
        <div className="flex space-x-2">
          {isMobile && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border ${showFilters ? 'bg-green-50 border-green-500 text-green-600' : 'border-gray-300 text-gray-500'}`}
            >
              <FiFilter size={18} />
            </button>
          )}
          <button
            onClick={openCreateOrderModal}
            className={isMobile 
              ? "p-2 bg-blue-600 text-white rounded-lg"
              : "flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            }
          >
            <FiPlus size={isMobile ? 18 : 16} />
            {!isMobile && <span>Tạo đơn</span>}
          </button>
          <button
            onClick={loadOrders}
            className={isMobile 
              ? "p-2 bg-green-600 text-white rounded-lg"
              : "flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            }
          >
            <FiRefreshCw size={isMobile ? 18 : 16} />
            {!isMobile && <span>Làm mới</span>}
          </button>
        </div>
      </div>

      {/* Status Tabs */}
      <div className={isMobile ? "bg-white px-3 py-2 overflow-x-auto" : "flex space-x-2 overflow-x-auto pb-2"}>
        <div className={isMobile ? "flex space-x-1" : "flex space-x-2"}>
          {['pending', 'preparing', 'completed', 'paid'].map((f) => (
            <button
              key={f}
              onClick={() => handleFilterChange(f)}
              className={isMobile 
                ? `px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    filter === f ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`
                : `px-4 py-2 rounded-lg whitespace-nowrap ${
                    filter === f ? 'bg-green-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`
              }
            >
              {f === 'pending' ? (isMobile ? 'Chờ' : 'Chờ xác nhận') : 
               f === 'preparing' ? (isMobile ? 'Làm' : 'Đang làm') : 
               f === 'completed' ? (isMobile ? 'Xong' : 'Hoàn thành') : 
               (isMobile ? 'Paid' : 'Đã thanh toán')}
            </button>
          ))}
        </div>
      </div>

      {/* Additional Filters */}
      {filter === 'completed' && (
        <div className={isMobile 
          ? `${showFilters ? 'bg-white px-4 py-3 border-b' : 'hidden'}`
          : "bg-white p-4 rounded-lg shadow flex items-center space-x-4"
        }>
          {isMobile ? (
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="all">Tất cả bàn</option>
              {tables.map(table => (
                <option key={table._id} value={table.tableNumber}>
                  {table.name || `Bàn ${table.tableNumber}`}
                </option>
              ))}
            </select>
          ) : (
            <>
              <FiFilter className="text-gray-500" />
              <span className="text-gray-600 font-medium">Lọc theo bàn:</span>
              <select
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="all">Tất cả bàn</option>
                {tables.map(table => (
                  <option key={table._id} value={table.tableNumber}>
                    {table.name || `Bàn ${table.tableNumber}`}
                  </option>
                ))}
              </select>
              {selectedTable !== 'all' && (
                <span className="text-sm text-gray-500">
                  ({filteredOrders.length} đơn)
                </span>
              )}
            </>
          )}
        </div>
      )}

      {filter === 'paid' && (
        <div className={isMobile 
          ? `${showFilters ? 'bg-white px-4 py-3 border-b space-y-3' : 'hidden'}`
          : "bg-white p-4 rounded-lg shadow"
        }>
          <div className={isMobile ? "space-y-3" : "flex flex-wrap items-center gap-4"}>
            <div className={isMobile ? "grid grid-cols-2 gap-2" : "flex items-center space-x-2"}>
              {!isMobile && <FiCalendar className="text-gray-500" />}
              <div className={isMobile ? "" : "flex items-center space-x-2"}>
                {!isMobile && <span className="text-gray-600 font-medium">Từ:</span>}
                <div 
                  className="relative cursor-pointer"
                  onClick={() => {
                    const dateInput = document.getElementById('legacy-paid-date-from');
                    if (dateInput) dateInput.showPicker();
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value={paidDateFrom ? (() => {
                      const [y, m, d] = paidDateFrom.split('-');
                      return `${d}/${m}/${y}`;
                    })() : ''}
                    placeholder="Từ ngày"
                    className={isMobile 
                      ? "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm cursor-pointer placeholder:text-gray-400"
                      : "border border-gray-300 rounded-lg px-3 py-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 placeholder:text-gray-400"
                    }
                  />
                  <input
                    id="legacy-paid-date-from"
                    type="date"
                    value={paidDateFrom}
                    onChange={(e) => { setPaidDateFrom(e.target.value); setPaidOrdersPage(1); setSelectedPaidOrders([]); }}
                    className="sr-only"
                  />
                </div>
              </div>
              <div className={isMobile ? "" : "flex items-center space-x-2"}>
                {!isMobile && <span className="text-gray-600 font-medium">Đến:</span>}
                <div 
                  className="relative cursor-pointer"
                  onClick={() => {
                    const dateInput = document.getElementById('legacy-paid-date-to');
                    if (dateInput) dateInput.showPicker();
                  }}
                >
                  <input
                    type="text"
                    readOnly
                    value={paidDateTo ? (() => {
                      const [y, m, d] = paidDateTo.split('-');
                      return `${d}/${m}/${y}`;
                    })() : ''}
                    placeholder="Đến ngày"
                    className={isMobile 
                      ? "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm cursor-pointer placeholder:text-gray-400"
                      : "border border-gray-300 rounded-lg px-3 py-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 placeholder:text-gray-400"
                    }
                  />
                  <input
                    id="legacy-paid-date-to"
                    type="date"
                    value={paidDateTo}
                    onChange={(e) => { setPaidDateTo(e.target.value); setPaidOrdersPage(1); setSelectedPaidOrders([]); }}
                    min={paidDateFrom}
                    className="sr-only"
                  />
                </div>
              </div>
            </div>
            <div className={isMobile ? "" : "flex items-center space-x-2"}>
              {!isMobile && <FiCreditCard className="text-gray-500" />}
              {!isMobile && <span className="text-gray-600 font-medium">Thanh toán:</span>}
              <select
                value={paidPaymentFilter}
                onChange={(e) => { setPaidPaymentFilter(e.target.value); setPaidOrdersPage(1); setSelectedPaidOrders([]); }}
                className={isMobile 
                  ? "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  : "border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                }
              >
                <option value="all">Tất cả</option>
                <option value="cash">Tiền mặt</option>
                <option value="transfer">Chuyển khoản</option>
                <option value="debt">Ghi nợ</option>
                <option value="hasDebt">Có khoản nợ</option>
              </select>
            </div>
            
            {/* Search box */}
            <div className={isMobile ? "" : "flex items-center space-x-2"}>
              {!isMobile && <FiSearch className="text-gray-500" />}
              <div className="relative">
                <input
                  type="text"
                  value={paidSearchTerm}
                  onChange={(e) => setPaidSearchTerm(e.target.value)}
                  placeholder="Tìm mã đơn, khách hàng, sản phẩm..."
                  className={isMobile 
                    ? "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pr-8"
                    : "border border-gray-300 rounded-lg px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-green-500 pr-8"
                  }
                />
                {paidSearchTerm && (
                  <button
                    onClick={() => setPaidSearchTerm('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FiX size={16} />
                  </button>
                )}
              </div>
            </div>
            
            {(paidDateFrom || paidDateTo || paidPaymentFilter !== 'all' || paidSearchTerm) && (
              <button
                onClick={() => { setPaidDateFrom(''); setPaidDateTo(''); setPaidPaymentFilter('all'); setPaidSearchTerm(''); setPaidOrdersPage(1); setSelectedPaidOrders([]); }}
                className="text-sm text-red-500 hover:text-red-700"
              >
                Xóa bộ lọc
              </button>
            )}
            
            {/* Export and Bulk delete buttons */}
            <div className="ml-auto flex items-center gap-2">
              {/* Export button */}
              <button
                onClick={handleExportExcel}
                disabled={exporting}
                className={`flex items-center px-4 py-2 rounded-lg transition ${
                  isMobile ? 'px-3 py-1.5 text-sm' : ''
                } ${exporting 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                <FiDownload className={isMobile ? 'mr-1' : 'mr-2'} size={isMobile ? 14 : 16} />
                {exporting ? 'Đang xuất...' : 'Xuất Excel'}
              </button>
              
              {/* Bulk delete button */}
              {selectedPaidOrders.length > 0 && (
                <button
                  onClick={() => setShowBulkDeleteModal(true)}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                >
                  <FiTrash2 className="mr-2" />
                  Xóa {selectedPaidOrders.length} đơn
                </button>
              )}
            </div>
          </div>
          
          {/* Summary stats when filter is applied */}
          {(paidDateFrom || paidDateTo || paidPaymentFilter !== 'all' || paidSearchTerm) && (
            isMobile ? (
              // Mobile: Grid layout
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-500">Số đơn</p>
                    <p className="font-bold text-blue-600">{paidOrdersTotal}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-500">Tiền mặt</p>
                    <p className="font-bold text-green-600 text-sm">
                      {formatCurrency(paidCashTotal)}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-500">Chuyển khoản</p>
                    <p className="font-bold text-blue-600 text-sm">
                      {formatCurrency(paidTransferTotal)}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-500">Ghi nợ</p>
                    <p className="font-bold text-orange-600 text-sm">
                      {formatCurrency(paidDebtTotal)}
                    </p>
                  </div>
                </div>
                {/* Points discount summary */}
                {paidPointsDiscountTotal > 0 && (
                  <div className="mt-2 bg-amber-50 rounded-lg p-2 text-center">
                    <p className="text-xs text-amber-700">Giảm giá điểm thưởng</p>
                    <p className="font-bold text-amber-600 text-sm">
                      -{formatCurrency(paidPointsDiscountTotal)}
                    </p>
                  </div>
                )}
                {!isSalesRole && (
                  <div className="mt-2 bg-green-100 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-600">Tổng doanh thu ({paidOrdersTotal} đơn)</p>
                    <p className="font-bold text-green-700">
                      {formatCurrency(filteredPaidRevenue)}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Desktop: Horizontal layout
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-6">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">Số đơn:</span>
                  <span className="font-bold text-blue-600">{paidOrdersTotal}</span>
                </div>
                {!isSalesRole && (
                  <>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Tiền mặt:</span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(paidCashTotal)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Chuyển khoản:</span>
                      <span className="font-bold text-blue-600">
                        {formatCurrency(paidTransferTotal)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Ghi nợ:</span>
                      <span className="font-bold text-orange-600">
                        {formatCurrency(paidDebtTotal)}
                      </span>
                    </div>
                    {paidPointsDiscountTotal > 0 && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">Giảm điểm:</span>
                        <span className="font-bold text-amber-600">
                          -{formatCurrency(paidPointsDiscountTotal)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 bg-green-50 px-4 py-2 rounded-lg">
                      <span className="text-gray-700 font-medium">Tổng doanh thu:</span>
                      <span className="font-bold text-green-700 text-lg">
                        {formatCurrency(filteredPaidRevenue)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            )
          )}
        </div>
      )}

      {/* Orders Grid - for non-paid tabs */}
      {filter !== 'paid' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredOrders.map((order) => {
          // Check if order has scheduled delivery and is "due soon" (<=30 min remaining) and not completed/paid
          const isPendingDelivery = order.deliveryAt && !['completed', 'paid', 'cancelled'].includes(order.status) && !order.paidAt;
          const minutesToDelivery = order.deliveryAt
            ? (new Date(order.deliveryAt).getTime() - Date.now()) / 60000
            : null;
          const isDeliverySoon = isPendingDelivery && minutesToDelivery !== null && minutesToDelivery <= 30;
          const isDeliveryOverdue = isPendingDelivery && minutesToDelivery !== null && minutesToDelivery < 0;
          return (
          <div 
            key={order._id} 
            ref={location.state?.highlightOrderId === order._id ? highlightOrderRef : null}
            className={`bg-white rounded-lg shadow-md p-4 transition-all duration-300 ${
              isDeliverySoon ? 'border-2 border-red-500 ring-2 ring-red-200' : ''
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-gray-800">
                    {order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi')}
                  </h3>
                  {getStatusBadge(order.status)}
                  {/* Badge đã thanh toán cho đơn pending/preparing */}
                  {order.paidAt && ['pending', 'preparing'].includes(order.status) && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Đã TT
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {new Date(order.createdAt).toLocaleTimeString('vi-VN')} - {new Date(order.createdAt).toLocaleDateString('vi-VN')}
                </p>
                {/* Scheduled delivery time */}
                {order.deliveryAt && (
                  <p className={`text-xs mt-0.5 font-medium ${
                    isDeliveryOverdue ? 'text-red-700' : isDeliverySoon ? 'text-red-600' : 'text-orange-600'
                  }`}>
                    ⏰ Giao lúc: {new Date(order.deliveryAt).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} - {new Date(order.deliveryAt).toLocaleDateString('vi-VN')}
                    {isDeliveryOverdue && ' (Quá giờ!)'}
                    {!isDeliveryOverdue && isDeliverySoon && ` (còn ~${Math.max(0, Math.round(minutesToDelivery))} phút)`}
                  </p>
                )}
                {/* Customer info */}
                {order.customer ? (
                  <div className="flex items-center mt-1 text-xs flex-wrap gap-1">
                    <FiUser className="text-green-600" size={12} />
                    <span className="text-green-700 font-medium">{order.customer.name}</span>
                    {order.customer.balance > 0 && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">
                        TT: {formatCurrency(order.customer.balance)}
                      </span>
                    )}
                    <button
                      onClick={() => removeCustomer(order._id)}
                      className="text-red-500 hover:text-red-700"
                      title="Xóa khách hàng"
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => openCustomerModal(order)}
                    className="flex items-center mt-1 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    <FiPlus size={12} className="mr-0.5" />
                    Thêm khách hàng
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 ml-2">
                {/* Quick-pay button - for preparing orders not yet pre-paid */}
                {order.status === 'preparing' && !order.paidAt && (
                  <button
                    onClick={() => openPaymentModal(order)}
                    className="p-1.5 text-green-600 hover:bg-green-100 rounded"
                    title="Thanh toán trước"
                  >
                    <FiDollarSign size={16} />
                  </button>
                )}
                {/* Edit button - for all except cancelled and paid */}
                {!['cancelled', 'paid'].includes(order.status) && (
                  <button
                    onClick={() => editingOrderId === order._id ? cancelEditingOrder() : startEditingOrder(order)}
                    className={`p-1.5 rounded ${editingOrderId === order._id ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
                    title={editingOrderId === order._id ? 'Hủy chỉnh sửa' : 'Chỉnh sửa đơn'}
                  >
                    <FiEdit2 size={16} />
                  </button>
                )}
                {/* Delete button - for completed only (not paid) */}
                {order.status === 'completed' && (
                  <button
                    onClick={() => openDeleteOrderModal(order)}
                    className="p-1.5 text-red-500 hover:bg-red-100 rounded"
                    title="Xóa đơn hàng"
                  >
                    <FiTrash2 size={16} />
                  </button>
                )}
                {/* Print button - only for preparing, completed, paid */}
                {['preparing', 'completed', 'paid'].includes(order.status) && (
                  <button
                    onClick={() => handlePrint(order)}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                    title="In hóa đơn"
                  >
                    <FiPrinter size={16} />
                  </button>
                )}
                {/* Share button - only for paid orders */}
                {order.status === 'paid' && (
                  <button
                    onClick={() => { setSelectedOrderForShare(order); setShowShareModal(true); }}
                    className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"
                    title="Chia sẻ"
                  >
                    <FiShare2 size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-2 mb-3">
              {/* Use editingOrderItems when editing, otherwise order.items */}
              {(editingOrderId === order._id ? editingOrderItems : order.items).map((item, idx) => (
                <div key={idx} className="border-b pb-2">
                  <div className="flex justify-between items-start text-sm mb-1">
                    <div className="flex-1 flex items-center gap-1">
                      <span className="font-semibold text-sm">{item.name}</span>
                      {!editingOrderId && (
                        <span className="text-gray-600 text-xs"> x{item.quantity}</span>
                      )}
                      {/* Recipe icon - only show for pending/preparing orders and only if item has recipe */}
                      {(order.status === 'pending' || order.status === 'preparing') && !editingOrderId && hasRecipe(item) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            loadRecipeForItem(item);
                          }}
                          className="p-0.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded"
                          title="Xem công thức"
                        >
                          <FiBook size={14} />
                        </button>
                      )}
                    </div>
                    <span className="font-semibold text-sm">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                  
                  {/* Display all options - moved above quantity */}
                  <div className="flex flex-wrap gap-1 mb-1">
                    {item.selectedOption && (
                      <span className="text-xs bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded font-medium">
                        {item.selectedOption.name}
                      </span>
                    )}
                    {item.temperature && item.temperature !== 'Mặc định' && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                        {item.temperature}
                      </span>
                    )}
                    {item.sweetness && item.sweetness !== 'Mặc định' && (
                      <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">
                        {item.sweetness}
                      </span>
                    )}
                    {item.selectedToppings && item.selectedToppings.length > 0 && item.selectedToppings.map((topping, tidx) => (
                      <span key={tidx} className="text-xs bg-pink-100 text-pink-800 px-1.5 py-0.5 rounded">
                        +{topping.name}
                      </span>
                    ))}
                  </div>

                  {/* Quantity and price controls when editing */}
                  {editingOrderId === order._id && (
                    <div className="flex items-center mt-1 space-x-2 flex-wrap gap-y-1">
                      <button
                        onClick={() => updateItemQuantity(order._id, idx, item.quantity - 1)}
                        className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                      >
                        <FiMinus size={12} />
                      </button>
                      <span className="font-bold w-6 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateItemQuantity(order._id, idx, item.quantity + 1)}
                        className="w-6 h-6 bg-green-500 text-white rounded flex items-center justify-center hover:bg-green-600"
                      >
                        <FiPlus size={12} />
                      </button>
                      <span className="text-gray-400 mx-1">×</span>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={item.price}
                        onChange={(e) => updateItemPrice(order._id, idx, e.target.value)}
                        className="w-20 h-6 text-right text-sm border rounded px-1 focus:outline-none focus:border-blue-500"
                      />
                      <span className="text-xs text-gray-500">đ</span>
                      {item.priceEdited && (
                        <span className="text-xs text-orange-500">(đã sửa)</span>
                      )}
                      <button
                        onClick={() => deleteItemFromOrder(order._id, idx)}
                        className="ml-auto text-red-500 hover:text-red-700 p-1"
                        title="Xóa món"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Add item button when editing */}
              {editingOrderId === order._id && (
                <button
                  onClick={() => openAddItemModal(order)}
                  className="w-full py-1.5 border-2 border-dashed border-green-400 text-green-600 rounded-lg hover:bg-green-50 flex items-center justify-center gap-1 text-sm"
                >
                  <FiPlus size={14} />
                  <span>Thêm món</span>
                </button>
              )}
              
              {/* Discount selection when editing */}
              {editingOrderId === order._id && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="text-xs font-medium text-yellow-800 mb-1">🎁 Chọn khuyến mãi</div>
                  {activeDiscounts.length > 0 ? (
                    <>
                      <div className="space-y-1 max-h-24 overflow-y-auto">
                        {activeDiscounts.map(discount => (
                          <label 
                            key={discount._id} 
                            className={`flex items-center gap-2 p-1.5 rounded cursor-pointer text-xs ${
                              editingOrderDiscount?._id === discount._id ? 'bg-green-100 border border-green-300' : 'hover:bg-yellow-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`edit-discount-${order._id}`}
                              checked={editingOrderDiscount?._id === discount._id}
                              onChange={() => setEditingOrderDiscount(discount)}
                              className="text-green-600"
                            />
                            <span className="flex-1">{discount.name}</span>
                            <span className="text-green-600 font-medium">-{discount.percentage}%</span>
                          </label>
                        ))}
                      </div>
                      {editingOrderDiscount && (
                        <button
                          onClick={() => setEditingOrderDiscount(null)}
                          className="text-xs text-red-600 hover:text-red-800 mt-1"
                        >
                          ✕ Bỏ khuyến mãi
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-gray-500 italic">
                      {editingOrderDiscount ? (
                        <div className="flex items-center justify-between">
                          <span>Đang áp dụng: {editingOrderDiscount.name} (-{editingOrderDiscount.percentage}%)</span>
                          <button
                            onClick={() => setEditingOrderDiscount(null)}
                            className="text-red-600 hover:text-red-800 ml-2"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        'Không có chương trình khuyến mãi'
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Extra fee editing */}
              {editingOrderId === order._id && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="text-xs font-medium text-orange-800 mb-1">💰 Phí thêm</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      placeholder="Số tiền"
                      value={editingOrderExtraFee.amount || ''}
                      onChange={(e) => setEditingOrderExtraFee(prev => ({
                        ...prev,
                        amount: parseInt(e.target.value) || 0
                      }))}
                      className="border rounded px-2 py-1 text-xs"
                    />
                    <input
                      type="text"
                      placeholder="Nội dung"
                      value={editingOrderExtraFee.description}
                      onChange={(e) => setEditingOrderExtraFee(prev => ({
                        ...prev,
                        description: e.target.value
                      }))}
                      className="border rounded px-2 py-1 text-xs"
                    />
                  </div>
                  {editingOrderExtraFee.amount > 0 && (
                    <>
                      <label className="flex items-center gap-2 mt-2 text-xs text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingOrderExtraFee.excludeFromRevenue}
                          onChange={(e) => setEditingOrderExtraFee(prev => ({
                            ...prev,
                            excludeFromRevenue: e.target.checked,
                            expenseGroup: e.target.checked ? prev.expenseGroup : '',
                            expenseGroupName: e.target.checked ? prev.expenseGroupName : '',
                            recipient: e.target.checked ? prev.recipient : ''
                          }))}
                          className="rounded text-blue-600"
                        />
                        Không tính vào doanh thu
                      </label>
                      {editingOrderExtraFee.excludeFromRevenue && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <select
                            value={editingOrderExtraFee.expenseGroup}
                            onChange={(e) => {
                              const group = expenseGroups.find(g => g._id === e.target.value);
                              setEditingOrderExtraFee(prev => ({
                                ...prev,
                                expenseGroup: e.target.value,
                                expenseGroupName: group?.name || ''
                              }));
                            }}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="">-- Nhóm --</option>
                            {expenseGroups.map(group => (
                              <option key={group._id} value={group._id}>{group.name}</option>
                            ))}
                          </select>
                          <select
                            value={editingOrderExtraFee.recipient}
                            onChange={(e) => setEditingOrderExtraFee(prev => ({
                              ...prev,
                              recipient: e.target.value
                            }))}
                            className="border rounded px-2 py-1 text-xs"
                          >
                            <option value="">-- Người nhận --</option>
                            {expenseRecipients.map(recipient => (
                              <option key={recipient} value={recipient}>{recipient}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Manual discount editing */}
              {editingOrderId === order._id && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-xs font-medium text-green-800">🏷️ Giảm giá</div>
                    {editingOrderManualDiscount.value && parseFloat(editingOrderManualDiscount.value) > 0 && (
                      <button
                        onClick={() => setEditingOrderManualDiscount({ type: 'fixed', value: '', amount: 0 })}
                        className="text-xs text-red-500"
                      >
                        ✕ Xóa
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex bg-white rounded border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setEditingOrderManualDiscount(prev => ({ ...prev, type: 'fixed' }))}
                        className={`px-2 py-1 text-xs font-medium ${
                          editingOrderManualDiscount.type === 'fixed' ? 'bg-green-600 text-white' : 'text-gray-600'
                        }`}
                      >
                        VNĐ
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingOrderManualDiscount(prev => ({ ...prev, type: 'percent' }))}
                        className={`px-2 py-1 text-xs font-medium ${
                          editingOrderManualDiscount.type === 'percent' ? 'bg-green-600 text-white' : 'text-gray-600'
                        }`}
                      >
                        %
                      </button>
                    </div>
                    <div className="flex-1 relative">
                      <input
                        type="number"
                        placeholder={editingOrderManualDiscount.type === 'fixed' ? 'Số tiền' : 'Phần trăm'}
                        value={editingOrderManualDiscount.value}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (editingOrderManualDiscount.type === 'percent' && parseFloat(val) > 100) {
                            val = '100';
                          }
                          setEditingOrderManualDiscount(prev => ({ ...prev, value: val }));
                        }}
                        className="w-full border rounded px-2 py-1 text-xs pr-6"
                      />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                        {editingOrderManualDiscount.type === 'fixed' ? 'đ' : '%'}
                      </span>
                    </div>
                  </div>
                  {editingOrderManualDiscount.value && parseFloat(editingOrderManualDiscount.value) > 0 && (
                    <div className="mt-1 text-xs text-green-700">
                      Giảm: -{formatCurrency(getEditingOrderManualDiscountAmount())}
                    </div>
                  )}
                </div>
              )}
            </div>

            {order.customerNote && (
              <div className="mb-3 p-2 bg-gray-50 rounded">
                <p className="text-xs text-gray-600"><strong>Ghi chú:</strong> {order.customerNote}</p>
              </div>
            )}

            <div className="flex justify-between items-center pt-3 border-t">
              <div>
                {/* Show discount info - use editing state if editing */}
                {editingOrderId === order._id ? (
                  editingOrderDiscount && (
                    <div className="text-xs text-green-600 mb-0.5">
                      🎁 {editingOrderDiscount.name} (-{editingOrderDiscount.percentage}%): -{formatCurrency(getEditingOrderDiscountAmount())}
                    </div>
                  )
                ) : (
                  order.discount && order.discount.amount > 0 && (
                    <div className="text-xs text-green-600 mb-0.5">
                      🎁 {order.discount.name || `Giảm ${order.discount.percentage}%`}: -{formatCurrency(order.discount.amount)}
                    </div>
                  )
                )}
                {/* Show manual discount info */}
                {editingOrderId === order._id ? (
                  getEditingOrderManualDiscountAmount() > 0 && (
                    <div className="text-xs text-green-600 mb-0.5">
                      🏷️ Giảm giá ({editingOrderManualDiscount.type === 'percent' ? `${editingOrderManualDiscount.value}%` : 'cố định'}): -{formatCurrency(getEditingOrderManualDiscountAmount())}
                    </div>
                  )
                ) : (
                  order.manualDiscount && order.manualDiscount.amount > 0 && (
                    <div className="text-xs text-green-600 mb-0.5">
                      🏷️ Giảm giá ({order.manualDiscount.discountType === 'percent' ? `${order.manualDiscount.value}%` : 'cố định'}): -{formatCurrency(order.manualDiscount.amount)}
                    </div>
                  )
                )}
                {/* Show extra fee info */}
                {editingOrderId === order._id ? (
                  editingOrderExtraFee?.amount > 0 && (
                    <div className="text-xs text-orange-600 mb-0.5">
                      💰 {editingOrderExtraFee.description || 'Phí thêm'}: +{formatCurrency(editingOrderExtraFee.amount)}
                      {editingOrderExtraFee.excludeFromRevenue && <span className="text-gray-500"> (Chi phí)</span>}
                    </div>
                  )
                ) : (
                  order.extraFee && order.extraFee.amount > 0 && (
                    <div className="text-xs text-orange-600 mb-0.5">
                      💰 {order.extraFee.description || 'Phí thêm'}: +{formatCurrency(order.extraFee.amount)}
                      {order.extraFee.excludeFromRevenue && <span className="text-gray-500"> (Chi phí)</span>}
                    </div>
                  )
                )}
                <span className="text-base font-bold text-green-600">
                  {formatCurrency(editingOrderId === order._id ? getEditingOrderTotal() : order.totalAmount)}
                </span>
              </div>
              <div className="flex gap-1.5">
                {/* Save/Cancel buttons when editing */}
                {editingOrderId === order._id ? (
                  <>
                    <button
                      onClick={cancelEditingOrder}
                      className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 text-xs"
                    >
                      Hủy
                    </button>
                    <button
                      onClick={() => saveEditedOrder(order._id)}
                      className="px-2.5 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs flex items-center gap-1"
                    >
                      <FiCheck size={12} />
                      <span>Lưu</span>
                    </button>
                  </>
                ) : (
                  <>
                    {order.status === 'pending' && (
                      <>
                        <button
                          onClick={() => cancelOrder(order._id, order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi'))}
                          className="px-2.5 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs flex items-center gap-1"
                        >
                          <FiX size={12} />
                          <span>Hủy</span>
                        </button>
                        <button
                          onClick={() => updateOrderStatus(order._id, 'preparing')}
                          className="px-2.5 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                        >
                          Nhận đơn
                        </button>
                      </>
                    )}
                    {order.status === 'preparing' && (
                      <>
                        <button
                          onClick={() => cancelOrder(order._id, order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi'))}
                          className="px-2.5 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200 text-xs flex items-center gap-1"
                        >
                          <FiX size={12} />
                          <span>Hủy</span>
                        </button>
                        <button
                          onClick={() => updateOrderStatus(order._id, 'completed')}
                          className="px-2.5 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                        >
                          Hoàn thành
                        </button>
                      </>
                    )}
                    {order.status === 'completed' && (
                      <button
                        onClick={() => openPaymentModal(order)}
                        className="px-2.5 py-1 bg-gray-800 text-white rounded hover:bg-gray-900 text-xs flex items-center gap-1"
                      >
                        <FiCheck size={12} />
                        <span>Thanh toán</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
      )}

      {/* Paid Orders List - with scroll load */}
      {filter === 'paid' && (
        isMobile ? (
          // Mobile: Card list view
          <div 
            ref={paidOrdersListRef}
            className="pb-20"
            style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
            onScroll={handlePaidOrdersScroll}
          >
            {/* Bulk Select Mode Header - Mobile */}
            {paidBulkSelectMode && (
              <div className="bg-red-500 text-white px-4 py-3 flex items-center justify-between sticky top-0 z-10">
                <div className="flex items-center space-x-3">
                  <button onClick={() => { setPaidBulkSelectMode(false); setSelectedPaidOrders([]); }} className="p-1">
                    <FiX size={20} />
                  </button>
                  <span className="font-medium">{selectedPaidOrders.length} đã chọn</span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => {
                      if (selectedPaidOrders.length === paidOrders.length) {
                        setSelectedPaidOrders([]);
                      } else {
                        setSelectedPaidOrders(paidOrders.map(o => o._id));
                      }
                    }}
                    className="px-3 py-1 text-sm bg-white/20 rounded"
                  >
                    {selectedPaidOrders.length === paidOrders.length ? 'Bỏ chọn' : 'Chọn tất cả'}
                  </button>
                  <button
                    onClick={() => setShowBulkDeleteModal(true)}
                    disabled={selectedPaidOrders.length === 0}
                    className="p-2 rounded-full bg-white/20 disabled:opacity-50"
                  >
                    <FiTrash2 size={18} />
                  </button>
                </div>
              </div>
            )}
            
            {/* Long press hint */}
            {!paidBulkSelectMode && paidOrders.length > 0 && (
              <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                Giữ lâu để chọn nhiều đơn hàng
              </div>
            )}
            
            {/* Order cards */}
            <div className="divide-y divide-gray-100">
              {paidOrders.map((order) => (
                <MobilePaidOrderCard key={order._id} order={order} />
              ))}
            </div>
            
            {loadingMorePaid && (
              <div className="text-center py-4 text-gray-500 text-sm">
                <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-green-500 mr-2"></div>
                Đang tải thêm...
              </div>
            )}
            {!loadingMorePaid && hasMorePaidOrders && (
              <div className="text-center py-2 text-gray-400 text-xs">
                Cuộn xuống để tải thêm ({paidOrders.length}/{paidOrdersTotal} đơn)
              </div>
            )}
            {paidOrders.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500 text-sm">
                Không có đơn hàng nào
              </div>
            )}
          </div>
        ) : (
          // Desktop: Table view
          <div 
            ref={paidOrdersListRef}
            className="bg-white rounded-lg shadow overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 280px)' }}
            onScroll={handlePaidOrdersScroll}
          >
            <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }} onScroll={handlePaidOrdersScroll}>
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={paidOrders.length > 0 && selectedPaidOrders.length === paidOrders.length}
                        onChange={toggleSelectAllPaidOrders}
                        className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Thời gian</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mã đơn</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Bàn</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Khách hàng</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Sản phẩm</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Tổng tiền</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Thanh toán</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paidOrders.map((order) => (
                    <tr 
                      key={order._id} 
                      className={`hover:bg-gray-50 cursor-pointer ${selectedPaidOrders.includes(order._id) ? 'bg-red-50' : ''}`}
                      onClick={() => { setSelectedOrderDetail(order); setShowOrderDetailModal(true); }}
                    >
                      <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedPaidOrders.includes(order._id)}
                          onChange={() => toggleSelectPaidOrder(order._id)}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div>{new Date(order.createdAt).toLocaleDateString('vi-VN')}</div>
                        <div className="text-gray-500 text-xs">{new Date(order.createdAt).toLocaleTimeString('vi-VN')}</div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {(order.orderCode || order.importCode) ? (
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{order.orderCode || order.importCode}</span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        {order.table?.name || (order.tableNumber ? `Bàn ${order.tableNumber}` : 'Mang đi')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {order.customer?.name || <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="max-w-xs">
                          {order.items.slice(0, 2).map((item, idx) => (
                            <span key={idx} className="text-gray-600">
                              {item.name} x{item.quantity}{idx < Math.min(order.items.length, 2) - 1 ? ', ' : ''}
                            </span>
                          ))}
                          {order.items.length > 2 && (
                            <span className="text-gray-400"> +{order.items.length - 2} món</span>
                          )}
                          {order.extraFee && order.extraFee.amount > 0 && (
                            <div className="text-xs text-orange-600 mt-1">
                              💰 {order.extraFee.description || 'Phí thêm'}: +{formatCurrency(order.extraFee.amount)}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {order.pointsDiscount > 0 && (
                          <div className="text-xs text-gray-400 line-through">{formatCurrency(order.totalAmount)}</div>
                        )}
                        <div className="font-bold text-green-600">
                          {formatCurrency(order.totalAmount - (order.pointsDiscount || 0))}
                        </div>
                        {order.pointsUsed > 0 && (
                          <div className="text-xs text-amber-600">-{order.pointsUsed} điểm</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            order.paymentMethod === 'transfer' 
                              ? 'bg-blue-100 text-blue-700' 
                              : order.paymentMethod === 'debt'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {order.paymentMethod === 'transfer' ? (
                              <><FiCreditCard size={12} className="mr-1" /> CK</>
                            ) : order.paymentMethod === 'debt' ? (
                              <><FiFileText size={12} className="mr-1" /> Nợ</>
                            ) : (
                              <><FiDollarSign size={12} className="mr-1" /> TM</>
                            )}
                          </span>
                          {order.debtAmount > 0 && order.paymentMethod !== 'debt' && (
                            <span className="text-xs text-orange-600">+Nợ: {formatCurrency(order.debtAmount)}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => { setSelectedOrderForShare(order); setShowShareModal(true); }}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                            title="Chia sẻ"
                          >
                            <FiShare2 size={16} />
                          </button>
                          <button
                            onClick={() => handlePrint(order)}
                            className="p-1.5 text-gray-600 hover:bg-gray-200 rounded"
                            title="In hóa đơn"
                          >
                            <FiPrinter size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loadingMorePaid && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-green-500 mr-2"></div>
                  Đang tải thêm...
                </div>
              )}
              {!loadingMorePaid && hasMorePaidOrders && (
                <div className="text-center py-2 text-gray-400 text-xs">
                  Cuộn xuống để tải thêm ({paidOrders.length}/{paidOrdersTotal} đơn)
                </div>
              )}
              {paidOrders.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-500">
                  Không có đơn hàng nào
                </div>
              )}
            </div>
          </div>
        )
      )}

      {filter !== 'paid' && filteredOrders.length === 0 && !loading && (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">Không có đơn hàng nào</p>
        </div>
      )}

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        show={showCustomerModal}
        customers={customers}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        showAddCustomerForm={showAddCustomerForm}
        setShowAddCustomerForm={setShowAddCustomerForm}
        newCustomerForm={newCustomerForm}
        setNewCustomerForm={setNewCustomerForm}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={assignCustomer}
        onAddNewCustomer={handleAddNewCustomer}
      />

      {/* Create Order Modal - Using Portal to render directly to body */}
      {showCreateOrderModal && (
        <Portal>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-0 md:p-4">
          <div 
            className="bg-white w-full h-full md:rounded-lg md:w-full md:max-w-6xl md:max-h-[90vh] overflow-hidden flex flex-col"
            style={{
              paddingTop: 'env(safe-area-inset-top)',
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)'
            }}
          >
            {/* Header - Hide on mobile since MobileCreateOrderContent has its own */}
            {!isMobile && (
              <div className="flex justify-between items-center p-3 md:p-4 border-b bg-blue-50 flex-shrink-0">
                <h2 className="text-lg md:text-xl font-bold">Tạo đơn hàng mới</h2>
                <button onClick={() => setShowCreateOrderModal(false)} className="p-1">
                  <FiX size={24} />
                </button>
              </div>
            )}

            {/* Mobile Layout - Tabs */}
            {isMobile ? (
              <MobileCreateOrderContent
                menuSearchTerm={menuSearchTerm}
                setMenuSearchTerm={setMenuSearchTerm}
                selectedMenuCategory={selectedMenuCategory}
                setSelectedMenuCategory={setSelectedMenuCategory}
                categories={categories}
                getDisplayedMenuItems={getDisplayedMenuItems}
                selectItemToConfigure={selectItemToConfigure}
                configuringItem={configuringItem}
                setConfiguringItem={setConfiguringItem}
                itemOptions={itemOptions}
                setItemOptions={setItemOptions}
                toppings={toppings}
                toggleTopping={toggleTopping}
                getItemOptions={getItemOptions}
                addConfiguredItemToOrder={addConfiguredItemToOrder}
                getItemConfigPrice={getItemConfigPrice}
                formatCurrency={formatCurrency}
                tables={tables}
                customers={customers}
                createOrderForm={createOrderForm}
                setCreateOrderForm={setCreateOrderForm}
                showNewCustomerInOrder={showNewCustomerInOrder}
                setShowNewCustomerInOrder={setShowNewCustomerInOrder}
                newCustomerForm={newCustomerForm}
                setNewCustomerForm={setNewCustomerForm}
                createNewCustomerInOrder={createNewCustomerInOrder}
                createOrderCustomerSearch={createOrderCustomerSearch}
                setCreateOrderCustomerSearch={setCreateOrderCustomerSearch}
                searchCreateOrderCustomers={searchCreateOrderCustomers}
                showCustomerSearchDropdown={showCustomerSearchDropdown}
                setShowCustomerSearchDropdown={setShowCustomerSearchDropdown}
                getFilteredCustomersForOrder={getFilteredCustomersForOrder}
                selectCustomerForOrder={selectCustomerForOrder}
                clearSelectedCustomer={clearSelectedCustomer}
                removeCreateOrderItem={removeCreateOrderItem}
                updateCreateOrderItemQty={updateCreateOrderItemQty}
                updateCreateOrderItemPrice={updateCreateOrderItemPrice}
                getCreateOrderTotal={getCreateOrderTotal}
                getCreateOrderDiscount={getCreateOrderDiscount}
                getCreateOrderFinalTotal={getCreateOrderFinalTotal}
                getCreateOrderExtraFee={getCreateOrderExtraFee}
                selectedCustomerDiscount={selectedCustomerDiscount}
                setSelectedCustomerDiscount={setSelectedCustomerDiscount}
                activeDiscounts={activeDiscounts}
                expenseGroups={expenseGroups}
                expenseRecipients={expenseRecipients}
                submitCreateOrder={submitCreateOrder}
                menuListRef={menuListRef}
                handleMenuScroll={handleMenuScroll}
                displayedItemsCount={displayedItemsCount}
                getFilteredMenuItems={getFilteredMenuItems}
                onClose={() => setShowCreateOrderModal(false)}
              />
            ) : (
              /* Desktop Layout - 3 columns */
              <div className="flex-1 overflow-hidden flex">
              {/* Left: Menu Section */}
              <div className="w-1/3 border-r flex flex-col">
                <div className="p-3 border-b space-y-2">
                  {/* Search */}
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Tìm món..."
                      value={menuSearchTerm}
                      onChange={(e) => setMenuSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                    />
                  </div>

                  {/* Categories Dropdown */}
                  <select
                    value={selectedMenuCategory}
                    onChange={(e) => setSelectedMenuCategory(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="all">Tất cả danh mục</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Menu Items */}
                <div 
                  className="flex-1 overflow-y-auto p-2"
                  ref={menuListRef}
                  onScroll={handleMenuScroll}
                >
                  <div className="space-y-1">
                    {getDisplayedMenuItems().map(item => (
                      <button
                        key={item._id}
                        onClick={() => selectItemToConfigure(item)}
                        className={`w-full text-left p-2 border rounded-lg text-sm ${
                          configuringItem?._id === item._id 
                            ? 'border-green-500 bg-green-50' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium truncate">{item.name}</div>
                        <div className="text-green-600 font-bold">{formatCurrency(item.price)}</div>
                      </button>
                    ))}
                    {/* Load more indicator */}
                    {displayedItemsCount < getFilteredMenuItems().length && (
                      <div className="text-center py-2 text-gray-400 text-xs">
                        Cuộn để xem thêm ({displayedItemsCount}/{getFilteredMenuItems().length} món)
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Middle: Item Options */}
              <div className="w-1/3 border-r flex flex-col bg-gray-50">
                <div className="p-2 border-b flex justify-between items-center">
                  <h3 className="font-bold text-sm">Tùy chọn món</h3>
                  {configuringItem && (
                    <button
                      onClick={() => setConfiguringItem(null)}
                      className="text-gray-500 hover:text-red-500 p-1"
                      title="Đóng"
                    >
                      <FiX size={18} />
                    </button>
                  )}
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                  {!configuringItem ? (
                    <p className="text-center text-gray-500 py-8 text-sm">Chọn món từ menu</p>
                  ) : (
                    <div className="space-y-2">
                      {/* Item name only */}
                      <div className="bg-white p-2 rounded border">
                        <span className="font-bold text-sm">{configuringItem.name}</span>
                      </div>

                      {/* Custom Options (Size) - inline */}
                      {configuringItem.customOptions?.length > 0 && (
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-gray-600">Size / Loại</label>
                          <div className="flex flex-wrap gap-1">
                            {configuringItem.customOptions.map((opt, idx) => (
                              <label key={idx} className={`flex items-center px-2 py-1 rounded border cursor-pointer text-xs ${itemOptions.selectedOption?.name === opt.name ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-blue-50'}`}>
                                <input
                                  type="radio"
                                  name="customOption"
                                  checked={itemOptions.selectedOption?.name === opt.name}
                                  onChange={() => setItemOptions({ ...itemOptions, selectedOption: opt })}
                                  className="hidden"
                                />
                                <span>{opt.name}</span>
                                {opt.price > 0 && <span className="ml-1 opacity-75">+{formatCurrency(opt.price)}</span>}
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Temperature - only show for drinks without ice exclusion */}
                      {getItemOptions(configuringItem).showIceOptions && (
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-gray-600">Mức đá</label>
                          <div className="flex flex-wrap gap-1">
                            {['Mặc định', 'Ít đá', 'Không đá', ...(configuringItem.hasHotOption ? ['Nóng'] : [])].map(temp => (
                              <button
                                key={temp}
                                onClick={() => setItemOptions({ ...itemOptions, temperature: temp })}
                                className={`px-2 py-1 rounded text-xs ${
                                  itemOptions.temperature === temp 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white border hover:bg-blue-50'
                                }`}
                              >
                                {temp === 'Nóng' ? '🔥 Nóng' : temp}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sweetness - only show for drinks without sweetness exclusion */}
                      {getItemOptions(configuringItem).showSweetnessOptions && (
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-gray-600">Mức đường</label>
                          <div className="flex flex-wrap gap-1">
                            {['Mặc định', 'Ít ngọt', 'Không đường', 'Thêm ngọt'].map(sweet => (
                              <button
                                key={sweet}
                                onClick={() => setItemOptions({ ...itemOptions, sweetness: sweet })}
                                className={`px-2 py-1 rounded text-xs ${
                                  itemOptions.sweetness === sweet 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-white border hover:bg-orange-50'
                                }`}
                              >
                                {sweet}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Toppings - vertical list */}
                      {toppings.length > 0 && getItemOptions(configuringItem).showToppingOptions && (
                        <div>
                          <label className="block text-xs font-semibold mb-1 text-gray-600">Topping</label>
                          <div className="space-y-1">
                            {toppings.filter(t => t.isAvailable !== false).map(topping => (
                              <label key={topping._id} className={`flex items-center px-2 py-1.5 rounded border cursor-pointer text-xs ${itemOptions.selectedToppings.some(t => t._id === topping._id) ? 'bg-pink-100 border-pink-400' : 'bg-white hover:bg-pink-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={itemOptions.selectedToppings.some(t => t._id === topping._id)}
                                  onChange={() => toggleTopping(topping)}
                                  className="mr-1.5 w-3 h-3"
                                />
                                <span className="flex-1">{topping.name}</span>
                                <span className="text-pink-600 ml-1">+{formatCurrency(topping.price)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quantity - inline with button */}
                      <div className="flex items-center justify-between pt-2 border-t">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-semibold text-gray-600">SL:</span>
                          <button
                            onClick={() => setItemOptions({ ...itemOptions, quantity: Math.max(1, itemOptions.quantity - 1) })}
                            className="w-7 h-7 bg-gray-200 rounded flex items-center justify-center"
                          >
                            <FiMinus size={14} />
                          </button>
                          <input
                            type="number"
                            min="1"
                            value={itemOptions.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              setItemOptions({ ...itemOptions, quantity: Math.max(1, val) });
                            }}
                            className="w-12 h-7 text-center text-lg font-bold border rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                          <button
                            onClick={() => setItemOptions({ ...itemOptions, quantity: itemOptions.quantity + 1 })}
                            className="w-7 h-7 bg-green-600 text-white rounded flex items-center justify-center"
                          >
                            <FiPlus size={14} />
                          </button>
                        </div>
                        <button
                          onClick={addConfiguredItemToOrder}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold text-sm hover:bg-green-700"
                        >
                          Thêm {formatCurrency(getItemConfigPrice())}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Cart Section */}
              <div className="w-1/3 flex flex-col">
                <div className="p-3 border-b space-y-2">
                  {/* Table Select */}
                  <select
                    value={createOrderForm.tableId}
                    onChange={(e) => setCreateOrderForm({ ...createOrderForm, tableId: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">-- Chọn bàn --</option>
                    {tables.map(table => (
                      <option key={table._id} value={table._id}>
                        {table.name || `Bàn ${table.tableNumber}`}
                      </option>
                    ))}
                  </select>

                  {/* Customer Search */}
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <div className="relative">
                          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                          <input
                            type="text"
                            placeholder="Tìm khách hàng (tên, SĐT)..."
                            value={createOrderCustomerSearch}
                            onChange={(e) => {
                              searchCreateOrderCustomers(e.target.value);
                              setShowCustomerSearchDropdown(true);
                              if (!e.target.value) {
                                setCreateOrderForm(prev => ({ ...prev, customerId: '' }));
                              }
                            }}
                            onFocus={() => {
                              setShowCustomerSearchDropdown(true);
                              if (!createOrderCustomerSearch) {
                                searchCreateOrderCustomers('');
                              }
                            }}
                            onBlur={() => {
                              // Delay để cho phép click vào dropdown item được xử lý trước
                              setTimeout(() => setShowCustomerSearchDropdown(false), 150);
                            }}
                            className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm"
                            disabled={showNewCustomerInOrder}
                          />
                          {createOrderForm.customerId && (
                            <button
                              type="button"
                              onClick={clearSelectedCustomer}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              <FiX size={14} />
                            </button>
                          )}
                        </div>
                        {/* Customer Search Dropdown */}
                        {showCustomerSearchDropdown && !showNewCustomerInOrder && !createOrderForm.customerId && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {getFilteredCustomersForOrder().length === 0 ? (
                              <div className="p-2 text-sm text-gray-500 text-center">
                                {createOrderCustomerSearch ? 'Không tìm thấy khách hàng' : 'Đang tải...'}
                              </div>
                            ) : (
                              getFilteredCustomersForOrder().slice(0, 15).map(customer => (
                                <button
                                  key={customer._id}
                                  type="button"
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // Prevent blur from firing first
                                    selectCustomerForOrder(customer);
                                  }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                                    createOrderForm.customerId === customer._id ? 'bg-blue-50 text-blue-700' : ''
                                  }`}
                                >
                                  <div className="font-medium">{customer.name}</div>
                                  {customer.phone && (
                                    <div className="text-xs text-gray-500">{customer.phone}</div>
                                  )}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewCustomerInOrder(!showNewCustomerInOrder);
                          setShowCustomerSearchDropdown(false);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium ${
                          showNewCustomerInOrder 
                            ? 'bg-gray-200 text-gray-700' 
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                        title={showNewCustomerInOrder ? 'Hủy' : 'Tạo khách mới'}
                      >
                        {showNewCustomerInOrder ? <FiX size={16} /> : <FiPlus size={16} />}
                      </button>
                    </div>
                    
                    {/* New Customer Form */}
                    {showNewCustomerInOrder && (
                      <div className="bg-blue-50 p-2 rounded-lg space-y-2">
                        <input
                          type="text"
                          placeholder="Tên khách hàng *"
                          value={newCustomerForm.name}
                          onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Số điện thoại"
                          value={newCustomerForm.phone}
                          onChange={(e) => setNewCustomerForm({ ...newCustomerForm, phone: e.target.value })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                        <button
                          type="button"
                          onClick={createNewCustomerInOrder}
                          className="w-full py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700"
                        >
                          Tạo khách hàng
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-3 border-b bg-gray-50">
                  <h3 className="font-bold text-sm">Đơn hàng ({createOrderForm.items.reduce((sum, i) => sum + i.quantity, 0)} món)</h3>
                </div>

                <div className="flex-1 overflow-y-auto p-2">
                  {createOrderForm.items.length === 0 ? (
                    <p className="text-center text-gray-500 py-8 text-sm">Chưa có món nào</p>
                  ) : (
                    <div className="space-y-2">
                      {createOrderForm.items.map((item, idx) => (
                        <div key={idx} className="p-2 border rounded-lg bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-medium text-sm">{item.name}</div>
                              {/* Options display */}
                              <div className="flex flex-wrap gap-1 mt-1">
                                {item.selectedOption && (
                                  <span className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{item.selectedOption.name}</span>
                                )}
                                {item.temperature && item.temperature !== 'Mặc định' && (
                                  <span className="text-xs bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">{item.temperature}</span>
                                )}
                                {item.sweetness && item.sweetness !== 'Mặc định' && (
                                  <span className="text-xs bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">{item.sweetness}</span>
                                )}
                                {item.selectedToppings?.map((t, i) => (
                                  <span key={i} className="text-xs bg-pink-100 text-pink-800 px-1.5 py-0.5 rounded">{t.name}</span>
                                ))}
                              </div>
                            </div>
                            <button
                              onClick={() => removeCreateOrderItem(idx)}
                              className="text-red-500 hover:bg-red-100 rounded p-1"
                            >
                              <FiX size={14} />
                            </button>
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => updateCreateOrderItemQty(idx, -1)}
                                className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center hover:bg-gray-300"
                              >
                                <FiMinus size={12} />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => updateCreateOrderItemQty(idx, e.target.value, true)}
                                className="w-10 h-6 text-center font-bold text-sm border rounded focus:outline-none focus:border-blue-500"
                              />
                              <button
                                onClick={() => updateCreateOrderItemQty(idx, 1)}
                                className="w-6 h-6 bg-green-600 text-white rounded flex items-center justify-center hover:bg-green-700"
                              >
                                <FiPlus size={12} />
                              </button>
                              <span className="text-gray-400 text-xs mx-1">×</span>
                              <input
                                type="number"
                                min="0"
                                step="1000"
                                value={item.price}
                                onChange={(e) => updateCreateOrderItemPrice(idx, e.target.value)}
                                className="w-20 h-6 text-right font-bold text-sm border rounded focus:outline-none focus:border-blue-500 pr-1"
                              />
                            </div>
                            <span className="font-bold text-green-600 text-sm">= {formatCurrency(item.price * item.quantity)}</span>
                          </div>
                          {item.originalPrice && item.originalPrice !== item.price && (
                            <div className="text-right mt-1">
                              <span className="text-xs text-gray-400 line-through">{formatCurrency(item.originalPrice)}</span>
                              <span className="text-xs text-orange-500 ml-1">Đã sửa giá</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    placeholder="Ghi chú..."
                    value={createOrderForm.customerNote}
                    onChange={(e) => setCreateOrderForm({ ...createOrderForm, customerNote: e.target.value })}
                    className="w-full mt-2 border rounded-lg p-2 text-sm"
                    rows="2"
                  />

                  {/* Scheduled Delivery Time */}
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-red-700">⏰ Hẹn giờ giao hàng</span>
                      {(createOrderForm.deliveryDate || createOrderForm.deliveryTime) && (
                        <button
                          type="button"
                          onClick={() => setCreateOrderForm({ ...createOrderForm, deliveryDate: '', deliveryTime: '' })}
                          className="ml-auto text-xs text-red-500 hover:text-red-700"
                        >
                          ✕ Bỏ hẹn
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={createOrderForm.deliveryDate}
                        onChange={(e) => setCreateOrderForm({ ...createOrderForm, deliveryDate: e.target.value })}
                        className="border rounded-lg px-2 py-2 text-sm"
                      />
                      <input
                        type="time"
                        value={createOrderForm.deliveryTime}
                        onChange={(e) => setCreateOrderForm({ ...createOrderForm, deliveryTime: e.target.value })}
                        className="border rounded-lg px-2 py-2 text-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Discount Selection */}
                  {activeDiscounts.length > 0 && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm font-medium text-yellow-800 mb-2">🎁 Chương trình khuyến mãi</div>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {activeDiscounts.map(discount => (
                          <label 
                            key={discount._id} 
                            className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-yellow-100 ${
                              selectedCustomerDiscount?._id === discount._id ? 'bg-green-100 border border-green-300' : ''
                            }`}
                          >
                            <input
                              type="radio"
                              name="discount"
                              checked={selectedCustomerDiscount?._id === discount._id}
                              onChange={() => setSelectedCustomerDiscount(discount)}
                              className="text-green-600"
                            />
                            <div className="flex-1 text-sm">
                              <span className="font-medium">{discount.name}</span>
                              <span className="text-green-600 ml-2">-{discount.percentage}%</span>
                              {discount.customer && (
                                <span className="text-xs text-gray-500 ml-2">(Khách hàng riêng)</span>
                              )}
                              {discount.categories?.length > 0 && (
                                <span className="text-xs text-gray-500 ml-2">
                                  ({discount.categories.map(c => c.name).join(', ')})
                                </span>
                              )}
                            </div>
                          </label>
                        ))}
                        {selectedCustomerDiscount && (
                          <button
                            onClick={() => setSelectedCustomerDiscount(null)}
                            className="text-xs text-red-600 hover:text-red-800 mt-1"
                          >
                            ✕ Bỏ chọn khuyến mãi
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Manual Discount Section */}
                  <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-green-800">🏷️ Giảm giá</div>
                      {desktopManualDiscountValue && parseFloat(desktopManualDiscountValue) > 0 && (
                        <button
                          onClick={() => setDesktopManualDiscountValue('')}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          ✕ Xóa
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Toggle VND / % */}
                      <div className="flex bg-white rounded-lg border overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setDesktopManualDiscountType('fixed')}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                            desktopManualDiscountType === 'fixed' 
                              ? 'bg-green-600 text-white' 
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          VNĐ
                        </button>
                        <button
                          type="button"
                          onClick={() => setDesktopManualDiscountType('percent')}
                          className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                            desktopManualDiscountType === 'percent' 
                              ? 'bg-green-600 text-white' 
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          %
                        </button>
                      </div>
                      
                      {/* Input */}
                      <div className="flex-1 relative">
                        <input
                          type="number"
                          placeholder={desktopManualDiscountType === 'fixed' ? 'Số tiền giảm' : 'Phần trăm giảm'}
                          value={desktopManualDiscountValue}
                          onChange={(e) => {
                            let val = e.target.value;
                            // Limit percent to 100
                            if (desktopManualDiscountType === 'percent' && parseFloat(val) > 100) {
                              val = '100';
                            }
                            setDesktopManualDiscountValue(val);
                          }}
                          className="w-full border rounded px-2 py-1.5 text-sm pr-8"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                          {desktopManualDiscountType === 'fixed' ? 'đ' : '%'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Show calculated discount */}
                    {desktopManualDiscountValue && parseFloat(desktopManualDiscountValue) > 0 && (
                      <div className="mt-2 text-sm text-green-700 font-medium">
                        Giảm: -{formatCurrency(getDesktopManualDiscountAmount())}
                      </div>
                    )}
                  </div>

                  {/* Extra Fee Section */}
                  <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="text-sm font-medium text-orange-800 mb-2">💰 Phí thêm (tùy chọn)</div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="number"
                          placeholder="Số tiền"
                          value={createOrderForm.extraFee?.amount || ''}
                          onChange={(e) => setCreateOrderForm(prev => ({
                            ...prev,
                            extraFee: { ...prev.extraFee, amount: parseInt(e.target.value) || 0 }
                          }))}
                          className="w-1/2 border rounded px-2 py-1 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Nội dung phí"
                          value={createOrderForm.extraFee?.description || ''}
                          onChange={(e) => setCreateOrderForm(prev => ({
                            ...prev,
                            extraFee: { ...prev.extraFee, description: e.target.value }
                          }))}
                          className="w-1/2 border rounded px-2 py-1 text-sm"
                        />
                      </div>
                      
                      {createOrderForm.extraFee?.amount > 0 && (
                        <>
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={createOrderForm.extraFee?.excludeFromRevenue || false}
                              onChange={(e) => setCreateOrderForm(prev => ({
                                ...prev,
                                extraFee: { 
                                  ...prev.extraFee, 
                                  excludeFromRevenue: e.target.checked,
                                  expenseGroup: e.target.checked ? prev.extraFee.expenseGroup : '',
                                  expenseGroupName: e.target.checked ? prev.extraFee.expenseGroupName : '',
                                  recipient: e.target.checked ? prev.extraFee.recipient : ''
                                }
                              }))}
                              className="rounded"
                            />
                            <span className="text-orange-700">Không tính vào doanh thu (tạo chi phí)</span>
                          </label>
                          
                          {createOrderForm.extraFee?.excludeFromRevenue && (
                            <div className="space-y-2 pl-6 border-l-2 border-orange-200">
                              <select
                                value={createOrderForm.extraFee?.expenseGroup || ''}
                                onChange={(e) => {
                                  const group = expenseGroups.find(g => g._id === e.target.value);
                                  setCreateOrderForm(prev => ({
                                    ...prev,
                                    extraFee: { 
                                      ...prev.extraFee, 
                                      expenseGroup: e.target.value,
                                      expenseGroupName: group?.name || ''
                                    }
                                  }));
                                }}
                                className="w-full border rounded px-2 py-1 text-sm"
                              >
                                <option value="">-- Chọn nhóm chi phí --</option>
                                {expenseGroups.map(group => (
                                  <option key={group._id} value={group._id}>{group.name}</option>
                                ))}
                              </select>
                              
                              <select
                                value={createOrderForm.extraFee?.recipient || ''}
                                onChange={(e) => setCreateOrderForm(prev => ({
                                  ...prev,
                                  extraFee: { ...prev.extraFee, recipient: e.target.value }
                                }))}
                                className="w-full border rounded px-2 py-1 text-sm"
                              >
                                <option value="">-- Chọn người nhận --</option>
                                {expenseRecipients.map(recipient => (
                                  <option key={recipient} value={recipient}>{recipient}</option>
                                ))}
                              </select>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-3 border-t bg-gray-50">
                  {/* Show discount info if discount is selected */}
                  {(selectedCustomerDiscount || getCreateOrderExtraFee() > 0) && createOrderForm.items.length > 0 && (
                    <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tạm tính:</span>
                        <span>{formatCurrency(getCreateOrderTotal())}</span>
                      </div>
                      {selectedCustomerDiscount && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>🎁 {selectedCustomerDiscount.name} (-{selectedCustomerDiscount.percentage}%)</span>
                          <span>-{formatCurrency(getCreateOrderDiscount())}</span>
                        </div>
                      )}
                      {getDesktopManualDiscountAmount() > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>🏷️ Giảm giá ({desktopManualDiscountType === 'percent' ? `${desktopManualDiscountValue}%` : 'cố định'})</span>
                          <span>-{formatCurrency(getDesktopManualDiscountAmount())}</span>
                        </div>
                      )}
                      {getCreateOrderExtraFee() > 0 && (
                        <div className="flex justify-between text-sm text-orange-600">
                          <span>💰 {createOrderForm.extraFee?.description || 'Phí thêm'}</span>
                          <span>+{formatCurrency(getCreateOrderExtraFee())}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg mb-3">
                    <span>Tổng:</span>
                    <span className="text-green-600">{formatCurrency(getCreateOrderFinalTotal())}</span>
                  </div>
                  <button
                    onClick={submitCreateOrder}
                    disabled={createOrderForm.items.length === 0}
                    className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Tạo đơn hàng
                  </button>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
        </Portal>
      )}

      {/* Payment Method Modal */}
      {showPaymentModal && selectedOrderForPayment && (() => {
        // Calculate points to earn
        const pointsToEarn = pointSettings?.isEnabled && pointSettings?.amountPerPoint > 0
          ? Math.floor((selectedOrderForPayment.totalAmount - (usePoints * (pointSettings?.pointValue || 500))) / pointSettings.amountPerPoint)
          : 0;
        
        return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className={`sticky top-0 bg-white ${isMobile ? 'p-3' : 'p-4'} border-b flex justify-between items-center`}>
              <h2 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold`}>Xác nhận thanh toán</h2>
              <button onClick={() => setShowPaymentModal(false)}>
                <FiX size={isMobile ? 20 : 24} />
              </button>
            </div>

            <div className={`${isMobile ? 'p-3 space-y-3' : 'p-4 space-y-4'}`}>
              {/* Order Info */}
              <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gray-50 rounded-lg ${isMobile ? 'space-y-1' : 'space-y-2'}`}>
                <div className={`flex justify-between ${isMobile ? 'text-sm' : ''}`}>
                  <span className="text-gray-600">Bàn:</span>
                  <span className="font-bold">{selectedOrderForPayment.table?.name || (selectedOrderForPayment.tableNumber ? `Bàn ${selectedOrderForPayment.tableNumber}` : 'Mang đi')}</span>
                </div>
                {(paymentCustomer?.name || selectedOrderForPayment.customer?.name || selectedOrderForPayment.customerName) && (
                  <div className={`flex justify-between ${isMobile ? 'text-sm' : ''}`}>
                    <span className="text-gray-600">Khách:</span>
                    <span className="font-medium">{paymentCustomer?.name || selectedOrderForPayment.customer?.name || selectedOrderForPayment.customerName}</span>
                  </div>
                )}
                {/* Show customer balance if exists */}
                {paymentCustomer?.balance > 0 && (
                  <div className={`flex justify-between ${isMobile ? 'text-sm' : ''}`}>
                    <span className="text-gray-600">Tiền thừa có sẵn:</span>
                    <span className="font-medium text-blue-600">{formatCurrency(paymentCustomer.balance)}</span>
                  </div>
                )}
                {paymentCustomer?.debt > 0 && (
                  <div className={`flex justify-between ${isMobile ? 'text-sm' : ''}`}>
                    <span className="text-gray-600">Đang nợ:</span>
                    <span className="font-medium text-orange-600">{formatCurrency(paymentCustomer.debt)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>Tổng tiền:</span>
                  <span className={`font-bold text-orange-500 ${isMobile ? 'text-base' : 'text-lg'}`}>{formatCurrency(selectedOrderForPayment.totalAmount)}</span>
                </div>
                
                {/* Points info - show if customer has points */}
                {pointSettings?.isEnabled && paymentCustomer && paymentCustomer.points > 0 && (
                  <div className={`flex justify-between text-amber-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    <span>Số điểm đang có:</span>
                    <span className="font-medium">
                      {paymentCustomer.points} điểm ({formatCurrency(paymentCustomer.points * (pointSettings?.pointValue || 500))})
                    </span>
                  </div>
                )}
                
                {/* Points discount applied */}
                {usePoints > 0 && (
                  <div className={`flex justify-between text-green-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    <span>Giảm ({usePoints} điểm):</span>
                    <span className="font-medium">-{formatCurrency(usePoints * (pointSettings?.pointValue || 500))}</span>
                  </div>
                )}
                
                {/* Final amount to pay - after points discount */}
                {usePoints > 0 && (
                  <div className={`flex justify-between pt-1.5 border-t border-gray-300 ${isMobile ? 'text-sm' : ''}`}>
                    <span className="font-medium text-gray-700">Số tiền cần thanh toán:</span>
                    <span className={`${isMobile ? 'text-base' : 'text-lg'} font-bold text-green-600`}>
                      {formatCurrency(selectedOrderForPayment.totalAmount - (usePoints * (pointSettings?.pointValue || 500)))}
                    </span>
                  </div>
                )}
                
                {/* Points to earn */}
                {pointSettings?.isEnabled && pointsToEarn > 0 && (paymentCustomer || selectedOrderForPayment.customer) && (
                  <div className={`flex justify-between text-blue-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    <span>Điểm sẽ nhận sau thanh toán:</span>
                    <span className="font-medium">+{pointsToEarn} điểm</span>
                  </div>
                )}
              </div>

              {/* Use Balance Option - only if customer has balance */}
              {paymentCustomer && paymentCustomer.balance > 0 && (
                <div className={`${isMobile ? 'p-2' : 'p-3'} bg-blue-50 rounded-lg ${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useBalance}
                      onChange={(e) => setUseBalance(e.target.checked)}
                      className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-blue-600 rounded`}
                    />
                    <span className={`font-medium text-blue-800 ${isMobile ? 'text-sm' : ''}`}>
                      Sử dụng tiền thừa ({formatCurrency(Math.min(paymentCustomer.balance, selectedOrderForPayment.totalAmount))})
                    </span>
                  </label>
                  
                  {/* Show remaining to pay if using balance */}
                  {useBalance && (
                    <div className={`${isMobile ? 'p-1.5' : 'p-2'} bg-white rounded border ${isMobile ? 'text-xs' : 'text-sm'}`}>
                      <div className="flex justify-between">
                        <span>Tổng tiền:</span>
                        <span>{formatCurrency(selectedOrderForPayment.totalAmount)}</span>
                      </div>
                      <div className="flex justify-between text-blue-600">
                        <span>Trừ tiền thừa:</span>
                        <span>-{formatCurrency(calculatePaymentAmounts().balanceToUse)}</span>
                      </div>
                      <div className="flex justify-between font-bold border-t pt-1 mt-1">
                        <span>Còn phải trả:</span>
                        <span className="text-green-600">{formatCurrency(calculatePaymentAmounts().remainingToPay)}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Use Points Option - only if customer has points and system is enabled */}
              {pointSettings?.isEnabled && paymentCustomer && paymentCustomer.points > 0 && (
                <div className={`${isMobile ? 'p-2' : 'p-3'} bg-amber-50 rounded-lg ${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
                  <div className="flex items-center gap-2">
                    <FiGift size={isMobile ? 14 : 16} className="text-amber-500" />
                    <span className={`font-medium text-amber-800 ${isMobile ? 'text-sm' : ''}`}>Sử dụng điểm thưởng</span>
                  </div>
                  
                  {maxRedeemablePoints > 0 ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={usePoints || ''}
                        onChange={(e) => {
                          const val = Math.min(
                            Math.max(0, parseInt(e.target.value) || 0),
                            maxRedeemablePoints
                          );
                          setUsePoints(val);
                        }}
                        placeholder="Nhập số điểm muốn dùng"
                        className={`flex-1 border rounded-lg ${isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}`}
                        max={maxRedeemablePoints}
                      />
                      <button
                        onClick={() => setUsePoints(maxRedeemablePoints)}
                        className={`${isMobile ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'} bg-amber-500 text-white rounded-lg hover:bg-amber-600 whitespace-nowrap`}
                      >
                        {isMobile ? `Tối đa (${maxRedeemablePoints})` : `Dùng tối đa (${maxRedeemablePoints})`}
                      </button>
                    </div>
                  ) : (
                    <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-amber-600`}>
                      {paymentCustomer.points < (pointSettings?.minRedeemPoints || 10)
                        ? `Cần tối thiểu ${pointSettings?.minRedeemPoints || 10} điểm để đổi`
                        : 'Không thể dùng điểm cho đơn này'}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Methods - 3 options */}
              <div>
                <label className={`block font-semibold ${isMobile ? 'mb-1.5 text-sm' : 'mb-3'}`}>Phương thức thanh toán</label>
                <div className={`grid grid-cols-3 ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`${isMobile ? 'p-2' : 'p-3'} border-2 rounded-lg flex flex-col items-center ${isMobile ? '' : 'space-y-1'} transition-all ${
                      paymentMethod === 'cash' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiDollarSign size={isMobile ? 18 : 24} className={paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-400'} />
                    <span className={`${isMobile ? 'text-xs mt-0.5' : 'text-sm'} font-medium ${paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-600'}`}>
                      Tiền mặt
                    </span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('transfer')}
                    className={`${isMobile ? 'p-2' : 'p-3'} border-2 rounded-lg flex flex-col items-center ${isMobile ? '' : 'space-y-1'} transition-all ${
                      paymentMethod === 'transfer' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiCreditCard size={isMobile ? 18 : 24} className={paymentMethod === 'transfer' ? 'text-blue-600' : 'text-gray-400'} />
                    <span className={`${isMobile ? 'text-xs mt-0.5' : 'text-sm'} font-medium ${paymentMethod === 'transfer' ? 'text-blue-600' : 'text-gray-600'}`}>
                      Chuyển khoản
                    </span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('debt')}
                    className={`${isMobile ? 'p-2' : 'p-3'} border-2 rounded-lg flex flex-col items-center ${isMobile ? '' : 'space-y-1'} transition-all ${
                      paymentMethod === 'debt' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiFileText size={isMobile ? 18 : 24} className={paymentMethod === 'debt' ? 'text-orange-600' : 'text-gray-400'} />
                    <span className={`${isMobile ? 'text-xs mt-0.5' : 'text-sm'} font-medium ${paymentMethod === 'debt' ? 'text-orange-600' : 'text-gray-600'}`}>
                      Ghi nợ
                    </span>
                  </button>
                </div>
              </div>

              {/* Bank Account Selection - only show for super admin when transfer is selected and has multiple banks */}
              {paymentMethod === 'transfer' && isSuperAdmin && bankAccounts && bankAccounts.length > 1 && (
                <div className={`${isMobile ? 'space-y-1.5' : 'space-y-2'}`}>
                  <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
                    Tài khoản nhận tiền
                  </label>
                  <div className={`${isMobile ? 'space-y-1.5' : 'grid grid-cols-2 gap-2'}`}>
                    {bankAccounts.map((bank, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedBankAccount(bank)}
                        className={`w-full text-left ${isMobile ? 'p-2' : 'p-3'} border-2 rounded-lg transition-all ${
                          selectedBankAccount?.accountNumber === bank.accountNumber
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'} ${selectedBankAccount?.accountNumber === bank.accountNumber ? 'text-blue-700' : 'text-gray-800'}`}>
                          {bank.bankName}
                        </div>
                        <div className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 truncate`}>
                          {bank.accountNumber}
                        </div>
                        <div className={`${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 truncate`}>
                          {bank.accountName}
                        </div>
                        {bank.isDefault && (
                          <span className={`inline-block mt-1 ${isMobile ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} bg-blue-100 text-blue-700 rounded`}>
                            Mặc định
                          </span>
                        )}
                        {bank.isPrimary && !bank.isDefault && (
                          <span className={`inline-block mt-1 ${isMobile ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-0.5 text-xs'} bg-green-100 text-green-700 rounded`}>
                            Chính
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Cash: Amount Received Input */}
              {paymentMethod === 'cash' && (
                <div className={`${isMobile ? 'p-2 space-y-2' : 'p-3 space-y-3'} bg-green-50 rounded-lg`}>
                  <div>
                    <label className={`block ${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700 mb-1`}>
                      Số tiền nhận từ khách
                      {(useBalance && calculatePaymentAmounts().balanceToUse > 0) || usePoints > 0 ? (
                        <span className="text-green-600 ml-1">
                          (cần: {formatCurrency(calculatePaymentAmounts().remainingToPay)})
                        </span>
                      ) : null}
                    </label>
                    <input
                      type="number"
                      value={amountReceived}
                      onChange={(e) => setAmountReceived(e.target.value)}
                      placeholder={`Nhập số tiền (VD: ${formatCurrency(calculatePaymentAmounts().remainingToPay)})`}
                      className={`w-full ${isMobile ? 'px-2 py-1.5 text-sm' : 'px-3 py-2'} border rounded-lg focus:ring-2 focus:ring-green-500`}
                    />
                  </div>
                  
                  {/* Quick amount buttons */}
                  <div className={`flex flex-wrap ${isMobile ? 'gap-1.5' : 'gap-2'}`}>
                    {[50000, 100000, 200000, 500000].map(amount => (
                      <button
                        key={amount}
                        onClick={() => setAmountReceived(amount.toString())}
                        className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} bg-white border rounded-lg hover:bg-gray-50`}
                      >
                        {(amount/1000)}k
                      </button>
                    ))}
                    <button
                      onClick={() => setAmountReceived(calculatePaymentAmounts().remainingToPay.toString())}
                      className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} bg-green-100 text-green-700 border border-green-300 rounded-lg hover:bg-green-200`}
                    >
                      Đủ tiền
                    </button>
                  </div>

                  {/* Calculate change/debt */}
                  {amountReceived && (
                    <div className={`${isMobile ? 'pt-1.5' : 'pt-2'} border-t border-green-200 space-y-2`}>
                      {calculatePaymentAmounts().change > 0 && (
                        <>
                          <div className={`flex justify-between text-green-700 ${isMobile ? 'text-sm' : ''}`}>
                            <span>Tiền thừa:</span>
                            <span className="font-bold">{formatCurrency(calculatePaymentAmounts().change)}</span>
                          </div>
                          
                          {/* Checkbox: Đã trả lại tiền thừa */}
                          <label className={`flex items-center gap-2 ${isMobile ? 'p-1.5' : 'p-2'} bg-white rounded-lg border cursor-pointer hover:bg-gray-50`}>
                            <input
                              type="checkbox"
                              checked={changeReturned}
                              onChange={(e) => {
                                setChangeReturned(e.target.checked);
                                if (e.target.checked) {
                                  // Đã trả tiền thừa → reset keepChange
                                  setKeepChange(false);
                                  setSaveBalanceAmount('');
                                }
                              }}
                              className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-green-600 rounded`}
                            />
                            <span className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-gray-700`}>
                              Đã trả lại tiền thừa cho khách
                            </span>
                          </label>
                          
                          {/* Phần giữ lại tiền thừa - chỉ hiện khi không check "Đã trả lại" */}
                          {!changeReturned && (
                            <>
                              {paymentCustomer ? (
                                <div className={`space-y-2 ${isMobile ? 'p-1.5' : 'p-2'} bg-blue-50 border border-blue-200 rounded-lg`}>
                                  <p className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium text-blue-700`}>💰 Giữ lại tiền thừa cho khách</p>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={keepChange}
                                      onChange={(e) => {
                                        setKeepChange(e.target.checked);
                                        if (e.target.checked) {
                                          setSaveBalanceAmount(calculatePaymentAmounts().change.toString());
                                        } else {
                                          setSaveBalanceAmount('');
                                        }
                                      }}
                                      className="w-4 h-4 text-blue-600 rounded"
                                    />
                                    <span className="text-sm text-blue-700">
                                      Lưu tiền thừa vào tài khoản khách
                                    </span>
                                  </label>
                                  
                                  {/* Amount input when keepChange is checked */}
                                  {keepChange && (
                                    <div className="flex items-center gap-2 p-2 bg-white rounded-lg border">
                                      <span className="text-sm text-gray-600">Số tiền lưu:</span>
                                      <input
                                        type="number"
                                        value={saveBalanceAmount}
                                        onChange={(e) => {
                                          const val = Math.min(parseFloat(e.target.value) || 0, calculatePaymentAmounts().change);
                                          setSaveBalanceAmount(val > 0 ? val.toString() : '');
                                        }}
                                        max={calculatePaymentAmounts().change}
                                        className="flex-1 px-2 py-1 border rounded text-right font-medium"
                                      />
                                      <span className="text-sm text-gray-500">đ</span>
                                    </div>
                                  )}
                                  {keepChange && saveBalanceAmount && parseFloat(saveBalanceAmount) < calculatePaymentAmounts().change && (
                                    <div className="text-xs text-gray-600">
                                      Tiền còn phải trả khách: {formatCurrency(calculatePaymentAmounts().change - parseFloat(saveBalanceAmount))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded-lg">
                                  ⚠️ Cần gán khách hàng cho đơn để giữ lại tiền thừa
                                </p>
                              )}
                            </>
                          )}
                        </>
                      )}
                      {calculatePaymentAmounts().debt > 0 && (
                        <div className="flex justify-between text-orange-600">
                          <span>Còn nợ:</span>
                          <span className="font-bold">{formatCurrency(calculatePaymentAmounts().debt)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Debt or Partial Cash: Need Customer */}
              {(paymentMethod === 'debt' || (paymentMethod === 'cash' && calculatePaymentAmounts().debt > 0)) && (
                <div className="p-3 bg-orange-50 rounded-lg space-y-3">
                  <div className="flex items-center text-orange-700 mb-2">
                    <FiAlertCircle className="mr-2" />
                    <span className="text-sm font-medium">
                      {paymentMethod === 'debt' 
                        ? `Ghi nợ ${formatCurrency(calculatePaymentAmounts().remainingToPay)} cho khách` 
                        : `Ghi nợ ${formatCurrency(calculatePaymentAmounts().debt)} cho khách`}
                    </span>
                  </div>

                  {/* Show existing customer or select */}
                  {paymentCustomer ? (
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg border">
                      <div className="min-w-0 flex-1">
                        <div className={`font-medium truncate ${isMobile ? 'text-sm' : ''}`}>{paymentCustomer.name}</div>
                        {paymentCustomer.phone && <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>{paymentCustomer.phone}</div>}
                        {paymentCustomer.debt > 0 && (
                          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-orange-600`}>Đang nợ: {formatCurrency(paymentCustomer.debt)}</div>
                        )}
                      </div>
                      <button
                        onClick={() => setPaymentCustomer(null)}
                        className="text-gray-400 hover:text-red-500 ml-2"
                      >
                        <FiX size={isMobile ? 18 : 20} />
                      </button>
                    </div>
                  ) : !showNewCustomerInPayment ? (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={paymentCustomerSearch}
                        onChange={(e) => searchPaymentCustomers(e.target.value)}
                        placeholder="Tìm khách hàng (tên hoặc SĐT)..."
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500"
                      />
                      
                      {paymentCustomerResults.length > 0 && (
                        <div className="bg-white border rounded-lg max-h-32 overflow-y-auto">
                          {paymentCustomerResults.map(c => (
                            <button
                              key={c._id}
                              onClick={() => {
                                setPaymentCustomer(c);
                                setPaymentCustomerSearch('');
                                setPaymentCustomerResults([]);
                              }}
                              className="w-full p-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                            >
                              <div className="font-medium">{c.name}</div>
                              <div className="text-xs text-gray-500 flex justify-between">
                                <span>{c.phone || 'Chưa có SĐT'}</span>
                                {c.debt > 0 && <span className="text-orange-600">Nợ: {formatCurrency(c.debt)}</span>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <button
                        onClick={() => setShowNewCustomerInPayment(true)}
                        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-400 hover:text-orange-600"
                      >
                        + Tạo khách hàng mới
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 p-3 bg-white rounded-lg border">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-sm">Khách hàng mới</span>
                        <button onClick={() => setShowNewCustomerInPayment(false)} className="text-gray-400 hover:text-gray-600">
                          <FiX size={16} />
                        </button>
                      </div>
                      <input
                        type="text"
                        value={newPaymentCustomer.name}
                        onChange={(e) => setNewPaymentCustomer({...newPaymentCustomer, name: e.target.value})}
                        placeholder="Tên khách hàng *"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <input
                        type="text"
                        value={newPaymentCustomer.phone}
                        onChange={(e) => setNewPaymentCustomer({...newPaymentCustomer, phone: e.target.value})}
                        placeholder="Số điện thoại"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`sticky bottom-0 bg-white ${isMobile ? 'p-3' : 'p-4'} border-t`}>
              <div className={`flex ${isMobile ? 'space-x-2' : 'space-x-3'}`}>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className={`flex-1 ${isMobile ? 'py-2.5 text-sm' : 'py-3'} border rounded-lg hover:bg-gray-100`}
                >
                  Hủy
                </button>
                <button
                  onClick={completePayment}
                  disabled={processingPayment}
                  className={`flex-1 ${isMobile ? 'py-2.5 text-sm' : 'py-3'} text-white rounded-lg font-bold disabled:opacity-50 ${
                    paymentMethod === 'debt' ? 'bg-orange-600 hover:bg-orange-700' :
                    paymentMethod === 'transfer' ? 'bg-blue-600 hover:bg-blue-700' :
                    'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {processingPayment ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Delete Order Modal with Password */}
      {showDeleteOrderModal && orderToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-red-600">Xóa đơn hàng</h2>
            
            <div className="mb-4 p-3 bg-red-50 rounded-lg">
              <p className="text-red-800 text-sm">
                <strong>Cảnh báo:</strong> Hành động này không thể hoàn tác!
              </p>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Đơn hàng:</span>
                <span className="font-bold">
                  {orderToDelete.table?.name || (orderToDelete.tableNumber ? `Bàn ${orderToDelete.tableNumber}` : 'Mang đi')}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-gray-600">Thời gian:</span>
                <span>{new Date(orderToDelete.createdAt).toLocaleString('vi-VN')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tổng tiền:</span>
                <span className="font-bold text-green-600">{formatCurrency(orderToDelete.totalAmount)}</span>
              </div>
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-2">Nhập mật khẩu để xác nhận *</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleDeleteOrderWithPassword()}
                placeholder="Nhập mật khẩu admin"
                className="w-full border rounded-lg px-4 py-2"
                autoFocus
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteOrderModal(false);
                  setOrderToDelete(null);
                  setDeletePassword('');
                }}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-100"
              >
                Hủy
              </button>
              <button
                onClick={handleDeleteOrderWithPassword}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700"
              >
                Xóa đơn hàng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Modal for Paid Orders */}
      <OrderDetailModal
        show={showOrderDetailModal}
        order={selectedOrderDetail}
        onClose={() => { setShowOrderDetailModal(false); setSelectedOrderDetail(null); }}
        onShare={(order) => { 
          setSelectedOrderForShare(order); 
          setShowShareModal(true);
          setShowOrderDetailModal(false);
        }}
        onPrint={printReceipt}
        onRemotePrint={remotePrint}
        printerOnline={printerOnline}
        remotePrinting={remotePrinting}
        isMobile={isMobile}
        formatCurrency={formatCurrency}
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4 text-red-600">Xóa nhiều đơn hàng</h2>
            
            <div className="mb-4 p-3 bg-red-50 rounded-lg">
              <p className="text-red-800 text-sm">
                <strong>Cảnh báo:</strong> Bạn đang xóa <strong>{selectedPaidOrders.length}</strong> đơn hàng. Hành động này không thể hoàn tác!
              </p>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-gray-600 text-sm">
                Tổng tiền các đơn sẽ xóa: <strong className="text-red-600">
                  {formatCurrency(
                    paidOrders
                      .filter(o => selectedPaidOrders.includes(o._id))
                      .reduce((sum, o) => sum + o.totalAmount, 0)
                  )}
                </strong>
              </p>
            </div>

            <div className="mb-4">
              <label className="block font-semibold mb-2">Nhập mật khẩu để xác nhận *</label>
              <input
                type="password"
                value={bulkDeletePassword}
                onChange={(e) => setBulkDeletePassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBulkDelete()}
                placeholder="Nhập mật khẩu admin"
                className="w-full border rounded-lg px-4 py-2"
                autoFocus
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowBulkDeleteModal(false);
                  setBulkDeletePassword('');
                }}
                className="flex-1 py-3 border rounded-lg hover:bg-gray-100"
                disabled={bulkDeleting}
              >
                Hủy
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:bg-gray-400"
              >
                {bulkDeleting ? 'Đang xóa...' : `Xóa ${selectedPaidOrders.length} đơn`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedOrderForShare && (
        <ShareModal
          order={selectedOrderForShare}
          shopSettings={shopSettings}
          onClose={() => {
            setShowShareModal(false);
            setSelectedOrderForShare(null);
          }}
        />
      )}

      {/* Recipe Modal - View preparation steps */}
      {showRecipeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b bg-amber-50 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FiBook className="text-amber-600" size={20} />
                </div>
                <div>
                  <h2 className="font-bold text-gray-800">Công thức chế biến</h2>
                  {selectedRecipe && (
                    <p className="text-sm text-gray-600">
                      {selectedRecipe.itemName}
                      {selectedRecipe.sizeOption && ` (${selectedRecipe.sizeOption})`}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  setShowRecipeModal(false);
                  setSelectedRecipe(null);
                }}
                className="p-1 text-gray-500 hover:text-red-500 hover:bg-gray-100 rounded"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {loadingRecipe ? (
                <div className="flex items-center justify-center py-12">
                  <FiRefreshCw className="animate-spin text-amber-500" size={32} />
                </div>
              ) : selectedRecipe ? (
                <div className="space-y-4">
                  {/* Ingredients summary */}
                  {selectedRecipe.ingredients && selectedRecipe.ingredients.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-1">
                        📦 Nguyên liệu
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRecipe.ingredients.map((ing, idx) => (
                          <span key={idx} className="text-xs bg-white border px-2 py-1 rounded">
                            {ing.ingredientName || ing.ingredientId?.name}: {ing.amount} {ing.unit}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Supplies summary */}
                  {selectedRecipe.supplies && selectedRecipe.supplies.length > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <h3 className="font-semibold text-sm text-blue-700 mb-2 flex items-center gap-1">
                        🧴 Vật dụng
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedRecipe.supplies.map((sup, idx) => (
                          <span key={idx} className="text-xs bg-white border border-blue-200 px-2 py-1 rounded">
                            {sup.supplyName || sup.supplyId?.name}: {sup.quantity} {sup.unit}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preparation steps */}
                  {selectedRecipe.steps && selectedRecipe.steps.length > 0 ? (
                    <div>
                      <h3 className="font-semibold text-sm text-gray-700 mb-3 flex items-center gap-1">
                        👨‍🍳 Các bước chế biến
                      </h3>
                      <div className="space-y-3">
                        {selectedRecipe.steps.map((step, idx) => (
                          <div key={idx} className="flex gap-3">
                            <div className="flex-shrink-0 w-7 h-7 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                              {step.stepNumber || idx + 1}
                            </div>
                            <div className="flex-1 pt-0.5">
                              {step.title && (
                                <div className="font-medium text-gray-800 mb-1">{step.title}</div>
                              )}
                              {step.details && (
                                <div className="text-sm text-gray-600 whitespace-pre-wrap">{step.details}</div>
                              )}
                              {!step.title && !step.details && typeof step === 'string' && (
                                <div className="text-gray-800">{step}</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 rounded-lg p-3 text-center">
                      <p className="text-sm text-yellow-700">Chưa có các bước chế biến</p>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedRecipe.notes && (
                    <div className="bg-blue-50 rounded-lg p-3 mt-4">
                      <h3 className="font-semibold text-sm text-blue-700 mb-1">📝 Ghi chú</h3>
                      <p className="text-sm text-blue-600">{selectedRecipe.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Không tìm thấy công thức
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => {
                  setShowRecipeModal(false);
                  setSelectedRecipe(null);
                }}
                className="w-full py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item to Order Modal - Mobile-friendly layout */}
      {showAddItemModal && orderToAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center md:p-4">
          <div className="bg-white w-full h-full md:rounded-lg md:w-full md:max-w-4xl md:max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-3 md:p-4 border-b flex justify-between items-center bg-white">
              <h2 className="text-lg md:text-xl font-bold">
                Thêm món - {orderToAddItem.table?.name || (orderToAddItem.tableNumber ? `Bàn ${orderToAddItem.tableNumber}` : 'Mang đi')}
              </h2>
              <button
                onClick={() => {
                  setShowAddItemModal(false);
                  setOrderToAddItem(null);
                  setAddItemConfiguring(null);
                }}
                className="text-gray-500 hover:text-red-500 p-1"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Desktop: 2 columns | Mobile: Single column with conditional view */}
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Menu List - Hidden on mobile when configuring item */}
              <div className={`${addItemConfiguring ? 'hidden md:flex' : 'flex'} flex-col flex-1 md:w-1/2 md:border-r overflow-hidden`}>
                {/* Search & Category */}
                <div className="p-2 md:p-3 border-b space-y-2 bg-white">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Tìm món..."
                      value={addItemSearch}
                      onChange={(e) => setAddItemSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <select
                    value={addItemCategory}
                    onChange={(e) => setAddItemCategory(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    <option value="all">Tất cả danh mục</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* Menu Items Grid */}
                <div className="flex-1 overflow-y-auto p-2">
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                    {getFilteredAddItems().map(item => (
                      <button
                        key={item._id}
                        onClick={() => selectAddItem(item)}
                        className={`text-left p-2 md:p-2 border rounded-lg text-sm ${
                          addItemConfiguring?._id === item._id ? 'border-green-500 bg-green-50' : 'bg-white hover:bg-gray-50 active:bg-gray-100'
                        }`}
                      >
                        <div className="font-medium truncate">{item.name}</div>
                        <div className="text-green-600 font-bold">{formatCurrency(item.price)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Config Panel - Full width on mobile, half on desktop */}
              <div className={`${addItemConfiguring ? 'flex' : 'hidden md:flex'} flex-col flex-1 md:w-1/2 bg-gray-50 overflow-hidden`}>
                <div className="p-3 border-b bg-white md:bg-gray-50">
                  <h3 className="font-bold text-sm">Tùy chọn món</h3>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {!addItemConfiguring ? (
                    <p className="text-center text-gray-500 py-8 text-sm">Chọn món từ menu</p>
                  ) : (
                    <div className="space-y-3">
                      {/* Item name with close (mobile only) */}
                      <div className="bg-white p-3 rounded-lg border flex justify-between items-center">
                        <span className="font-bold">{addItemConfiguring.name}</span>
                        <button 
                          onClick={() => setAddItemConfiguring(null)} 
                          className="text-gray-400 md:hidden"
                        >
                          <FiX size={20} />
                        </button>
                      </div>

                      {/* Size */}
                      {addItemConfiguring.customOptions?.length > 0 && (
                        <div className="bg-white p-3 rounded-lg border">
                          <label className="block text-xs font-semibold mb-2 text-gray-600">Size / Loại</label>
                          <div className="flex flex-wrap gap-2">
                            {addItemConfiguring.customOptions.map((opt, idx) => (
                              <button
                                key={idx}
                                onClick={() => setAddItemOptions({ ...addItemOptions, selectedOption: opt })}
                                className={`px-3 py-2 rounded-lg text-sm ${
                                  addItemOptions.selectedOption?.name === opt.name 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                {opt.name}
                                {opt.price > 0 && <span className="ml-1 opacity-75">+{formatCurrency(opt.price)}</span>}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Temperature */}
                      {getItemOptions(addItemConfiguring).showIceOptions && (
                        <div className="bg-white p-3 rounded-lg border">
                          <label className="block text-xs font-semibold mb-2 text-gray-600">Mức đá</label>
                          <div className="flex flex-wrap gap-2">
                            {['Mặc định', 'Ít đá', 'Không đá', ...(addItemConfiguring.hasHotOption ? ['Nóng'] : [])].map(temp => (
                              <button
                                key={temp}
                                onClick={() => setAddItemOptions({ ...addItemOptions, temperature: temp })}
                                className={`px-3 py-2 rounded-lg text-sm ${
                                  addItemOptions.temperature === temp 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                {temp === 'Nóng' ? '🔥 Nóng' : temp}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Sweetness */}
                      {getItemOptions(addItemConfiguring).showSweetnessOptions && (
                        <div className="bg-white p-3 rounded-lg border">
                          <label className="block text-xs font-semibold mb-2 text-gray-600">Mức đường</label>
                          <div className="flex flex-wrap gap-2">
                            {['Mặc định', 'Ít ngọt', 'Không đường', 'Thêm ngọt'].map(sweet => (
                              <button
                                key={sweet}
                                onClick={() => setAddItemOptions({ ...addItemOptions, sweetness: sweet })}
                                className={`px-3 py-2 rounded-lg text-sm ${
                                  addItemOptions.sweetness === sweet 
                                    ? 'bg-orange-500 text-white' 
                                    : 'bg-gray-100 hover:bg-gray-200'
                                }`}
                              >
                                {sweet}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Toppings */}
                      {toppings.length > 0 && getItemOptions(addItemConfiguring).showToppingOptions && (
                        <div className="bg-white p-3 rounded-lg border">
                          <label className="block text-xs font-semibold mb-2 text-gray-600">Topping</label>
                          <div className="space-y-2">
                            {toppings.filter(t => t.isAvailable !== false).map(topping => (
                              <label 
                                key={topping._id} 
                                className={`flex items-center p-2 rounded-lg border cursor-pointer ${
                                  addItemOptions.selectedToppings.some(t => t._id === topping._id) 
                                    ? 'bg-pink-100 border-pink-400' 
                                    : 'bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={addItemOptions.selectedToppings.some(t => t._id === topping._id)}
                                  onChange={() => toggleAddItemTopping(topping)}
                                  className="mr-2 w-4 h-4"
                                />
                                <span className="flex-1 text-sm">{topping.name}</span>
                                <span className="text-pink-600 text-sm">+{formatCurrency(topping.price)}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Quantity & Add Button */}
                      <div className="bg-white p-3 rounded-lg border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium">Số lượng:</span>
                            <button
                              onClick={() => setAddItemOptions({ ...addItemOptions, quantity: Math.max(1, addItemOptions.quantity - 1) })}
                              className="w-9 h-9 bg-gray-200 rounded-lg flex items-center justify-center"
                            >
                              <FiMinus size={18} />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={addItemOptions.quantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value) || 1;
                                setAddItemOptions({ ...addItemOptions, quantity: Math.max(1, val) });
                              }}
                              className="w-14 h-9 text-center text-xl font-bold border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                            <button
                              onClick={() => setAddItemOptions({ ...addItemOptions, quantity: addItemOptions.quantity + 1 })}
                              className="w-9 h-9 bg-green-600 text-white rounded-lg flex items-center justify-center"
                            >
                              <FiPlus size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Fixed bottom button when configuring */}
                {addItemConfiguring && (
                  <div className="p-3 border-t bg-white">
                    <button
                      onClick={addItemToExistingOrder}
                      className="w-full py-3 bg-green-600 text-white rounded-lg font-bold text-base hover:bg-green-700"
                    >
                      Thêm món - {formatCurrency(getAddItemPrice())}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default OrderManagement;
